import React, { useEffect, useState, useCallback, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { Card, Row, Col, Alert, Spinner, Button } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faExclamationTriangle, faArrowUp, faArrowDown, faChevronDown, faArrowLeft, faEquals, faSearch } from "@fortawesome/free-solid-svg-icons";
import MainLayouts from "layouts/MainLayouts";
import CardWrapper from "components/CardWrapper";
import AddToCartButton from "components/AddToCartButton";
import { getUpgradeOptions, getClientServices, getProductGroups, setSelectedGroup, setSelectedProduct, setSelectedBillingCycle, setSelectedService, resetUpgradeSelection } from "features/upgrade/upgradeSlice";
import { addToCart } from "features/common/layouts/cartSlice";
import { formatIDR } from "helpers/utils";
import { notifyError, notifySuccess } from "components/ToastMessage";
import './style.css';
import { getProductSpecifications, compareSpecifications, getBaseProductName } from './data';

// Helper function to normalize billing cycle for pricing lookup
const normalizeBillingCycle = (cycle) => {
  const normalizedCycle = String(cycle).toLowerCase().trim();

  // Map WHMCS billing cycle variations to pricing keys
  const cycleMap = {
    'monthly': 'monthly',
    'quarterly': 'quarterly',
    'semiannually': 'semiannually',
    'semi-annually': 'semiannually',
    'annually': 'annually',
    'biennially': 'biennially',
    'triennially': 'triennially'
  };

  return cycleMap[normalizedCycle] || normalizedCycle;
};



// Helper function to calculate current price for products with configoptions
const calculateCurrentPrice = (currentService, isMonitimeProduct, isEmailProduct) => {

  // For Monitime products, calculate price based on configoptions
  if (isMonitimeProduct && currentService?.product_details?.configoptions?.configoption?.[0]) {
    const configOption = currentService.product_details.configoptions.configoption[0];
    // Get current config value from service configoptions (not product_details)
    const currentConfigValue = currentService?.configoptions?.configoption?.[0]?.value || configOption.value || 0;
    const billingCycle = normalizeBillingCycle(currentService.billingcycle);
    const pricePerMonitor = configOption.options?.option?.[0]?.pricing?.IDR?.[billingCycle] || 0;
    // Apply n-1 formula: config value 0 = 1 monitor, config value 1 = 2 monitors, etc.
    const actualMonitorCount = currentConfigValue + 1;
    return actualMonitorCount * pricePerMonitor;
  }

  // For Email products with configoptions, calculate price based on account count
  if (isEmailProduct && currentService?.product_details?.configoptions?.configoption) {
    let totalPrice = 0;
    const billingCycle = normalizeBillingCycle(currentService.billingcycle);

    currentService.product_details.configoptions.configoption.forEach((configOption) => {
      if (configOption.name === "Jumlah Akun" || configOption.name === "Email Accounts" || configOption.name === "Akun") {
        const currentConfigValue = currentService?.configoptions?.configoption?.find(c => c.id === configOption.id)?.value || configOption.value || 0;
        const pricePerAccount = configOption.options?.option?.[0]?.pricing?.IDR?.[billingCycle] || 0;
        totalPrice += currentConfigValue * pricePerAccount;
      }
    });

    if (totalPrice > 0) {
      return totalPrice;
    }
  }

  // For Email products without product_details (from list), use service amount if available
  if (isEmailProduct && !currentService?.product_details && currentService?.configoptions?.configoption) {
    let totalPrice = 0;

    currentService.configoptions.configoption.forEach((configOption) => {
      if (configOption.option === "Jumlah Akun" || configOption.option === "Email Accounts" || configOption.option === "Akun") {
        const currentConfigValue = configOption.value || 0;
        // Try to get price from service amount fields
        const amount = parseFloat(currentService?.amount || 0);
        const recurringamount = parseFloat(currentService?.recurringamount || 0);
        const firstpaymentamount = parseFloat(currentService?.firstpaymentamount || 0);

        // Use the first non-zero amount available
        const servicePrice = amount > 0 ? amount : (recurringamount > 0 ? recurringamount : firstpaymentamount);

        if (servicePrice > 0 && currentConfigValue > 0) {
          // For email products, the service price is already the total price for all accounts
          // So we just return the service price directly
          totalPrice = servicePrice;
        }
      }
    });

    if (totalPrice > 0) {
      return totalPrice;
    }
  }

  // For Monitime products without product_details (from list), use service amount if available
  if (isMonitimeProduct && !currentService?.product_details && currentService?.configoptions?.configoption?.[0]) {
    const currentConfigValue = currentService.configoptions.configoption[0].value || 0;
    const actualMonitorCount = currentConfigValue + 1;

    // Try to get price from service amount fields
    const amount = parseFloat(currentService?.amount || 0);
    const recurringamount = parseFloat(currentService?.recurringamount || 0);
    const firstpaymentamount = parseFloat(currentService?.firstpaymentamount || 0);

    // Use the first non-zero amount available
    const totalPrice = amount > 0 ? amount : (recurringamount > 0 ? recurringamount : firstpaymentamount);

    if (totalPrice > 0) {
      // Calculate price per monitor by dividing total price by actual monitor count
      const pricePerMonitor = totalPrice / actualMonitorCount;
      return pricePerMonitor * actualMonitorCount; // Return the same total price
    }

    // If no price available, return 0 instead of hardcoded values
    return 0;
  }

  // For other products, try to get price from product_details first, then fallback to service amount
  if (currentService?.product_details?.pricing?.IDR) {
    const billingCycle = normalizeBillingCycle(currentService.billingcycle);
    const priceFromProductDetails = currentService.product_details.pricing.IDR[billingCycle];
    if (priceFromProductDetails && parseFloat(priceFromProductDetails) > 0) {
      // console.log('calculateCurrentPrice Debug (Regular Product):', {
      //   productName: currentService.productname,
      //   billingCycle: currentService.billingcycle,
      //   normalizedBillingCycle: billingCycle,
      //   priceFromProductDetails: parseFloat(priceFromProductDetails),
      //   serviceAmount: currentService?.amount,
      //   usingProductDetails: true
      // });
      return parseFloat(priceFromProductDetails);
    }
  }

  // Fallback to service amount if product_details pricing is not available
  // console.log('calculateCurrentPrice Debug (Regular Product - Fallback):', {
  //   productName: currentService.productname,
  //   billingCycle: currentService.billingcycle,
  //   serviceAmount: currentService?.amount,
  //   recurringAmount: currentService?.recurringamount,
  //   usingProductDetails: false
  // });
  return currentService?.amount || currentService?.recurringamount || 0;
};

const Upgrade = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const {
    currentService,
    upgradeOptions,
    clientServices,
    productGroups,
    selectedService,
    selectedGroup,
    selectedProduct,
    selectedBillingCycle,
    upgradePrice,
    upgradeCalculationBreakdown,
    remainingDays,
    status,
    clientServicesStatus,
    productGroupsStatus,
    error,
  } = useSelector((state) => state.upgrade);

  // Auth state
  const isAuthenticated = useSelector((state) => state.auth.status_login);

  const [serviceid, setServiceid] = useState(null);
  const [targetid, setTargetid] = useState(null);
  const targetidRef = useRef(null); // Store targetid in ref to prevent re-fetch when cleared
  const [configOptionValue, setConfigOptionValue] = useState(1);
  const [isMonitimeProduct, setIsMonitimeProduct] = useState(false);
  const [isEmailProduct, setIsEmailProduct] = useState(false);
  const [isSpecialProduct1157, setIsSpecialProduct1157] = useState(false);
  const [monitimeUpgradePrice, setMonitimeUpgradePrice] = useState(0);
  const [emailUpgradePrice, setEmailUpgradePrice] = useState(0);
  const [special1157UpgradePrice, setSpecial1157UpgradePrice] = useState(0);
  const [showCalculationDetails, setShowCalculationDetails] = useState(false);
  const [showAllServices, setShowAllServices] = useState(false);
  const [serviceSearchQuery, setServiceSearchQuery] = useState('');
  const [redirectToInvoice, setRedirectToInvoice] = useState(null);
  const [upgradeToMonitime, setUpgradeToMonitime] = useState(false);
  const [special1157ConfigValue, setSpecial1157ConfigValue] = useState(1);
  const [special1157BillingCycle, setSpecial1157BillingCycle] = useState('monthly');
  
  // State for Velocity and Backup Harian options
  const [velocityEnabled, setVelocityEnabled] = useState(false);
  const [backupOption, setBackupOption] = useState('mingguan'); // 'mingguan' or 'harian'
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [baseSelectedProduct, setBaseSelectedProduct] = useState(null);
  const [velocityCost, setVelocityCost] = useState(0);
  const [backupCost, setBackupCost] = useState(0);
  const [isAutoSelecting, setIsAutoSelecting] = useState(false);
  const preservedBillingCycleRef = useRef(null); // Store billing cycle to restore after product switch

  // Helper function to extract base product name (remove addons)
  const extractBaseName = useCallback((productName) => {
    if (!productName) return '';
    let baseName = productName;
    
    // Remove addon suffixes
    baseName = baseName.replace(/\s*\+\s*Velocity\s*$/i, '');
    baseName = baseName.replace(/\s*\+\s*Backup\s+Harian\s*$/i, '');
    
    return baseName.trim();
  }, []);

  // Helper function to find product with specific addons
  const findProductWithAddons = useCallback((products, basePid, wantVelocity, wantBackup) => {
    console.log('🔍 findProductWithAddons called:', {
      basePid,
      wantVelocity,
      wantBackup,
      totalProducts: products.length
    });
    
    const baseProduct = products.find(p => p.pid === basePid);
    if (!baseProduct) {
      console.log('❌ Base product not found for PID:', basePid);
      return null;
    }
    
    const baseName = extractBaseName(baseProduct.name);
    console.log('📝 Base name extracted:', baseName, 'from', baseProduct.name);
    
    // Find product with same base name and specific addon combination
    const targetProduct = products.find(p => {
      const pName = p.name;
      const pBaseName = extractBaseName(pName);
      
      // Must have same base name
      if (pBaseName !== baseName) return false;
      
      const hasVelocity = pName.includes('+ Velocity');
      const hasBackup = pName.includes('+ Backup Harian');
      
      const matches = hasVelocity === wantVelocity && hasBackup === wantBackup;
      
      if (matches) {
        console.log('✅ Found matching product:', pName, 'PID:', p.pid);
      }
      
      // Must match the desired addon combination exactly
      return matches;
    });
    
    if (!targetProduct) {
      console.log('❌ No product found with addon combination. Checking available products with same base name:');
      products.filter(p => extractBaseName(p.name) === baseName).forEach(p => {
        const hasV = p.name.includes('+ Velocity');
        const hasB = p.name.includes('+ Backup Harian');
        console.log(`  - ${p.name} (PID: ${p.pid}) - Velocity: ${hasV}, Backup: ${hasB}`);
      });
    }
    
    return targetProduct;
  }, [extractBaseName]);

  // Helper function to find product with Velocity (for cost calculation)
  const findVelocityProduct = useCallback((products, basePid) => {
    const baseProduct = products.find(p => p.pid === basePid);
    if (!baseProduct) return null;
    
    const baseName = extractBaseName(baseProduct.name);
    
    // Find product with same base name but with "+ Velocity" (without Backup)
    const velocityProduct = products.find(p => {
      const pName = p.name;
      return pName.includes('+ Velocity') && 
             !pName.includes('+ Backup Harian') &&
             extractBaseName(pName) === baseName;
    });
    
    return velocityProduct;
  }, [extractBaseName]);

  // Helper function to find product with Backup Harian (for cost calculation)
  const findBackupProduct = useCallback((products, basePid) => {
    const baseProduct = products.find(p => p.pid === basePid);
    if (!baseProduct) return null;
    
    const baseName = extractBaseName(baseProduct.name);
    
    // Find product with same base name but with "+ Backup Harian" (without Velocity)
    const backupProduct = products.find(p => {
      const pName = p.name;
      return pName.includes('+ Backup Harian') && 
             !pName.includes('+ Velocity') &&
             extractBaseName(pName) === baseName;
    });
    
    return backupProduct;
  }, [extractBaseName]);

  // Check if product supports Velocity or Backup
  const productSupportsVelocity = (productName) => {
    return productName && (productName.includes('Proxima') || productName.includes('Core'));
  };

  const productSupportsBackup = (productName) => {
    return productName && 
      (productName.includes('Micro') || productName.includes('Starter') || 
       productName.includes('Lite') || productName.includes('SOHO') ||
       productName.includes('Proxima') || productName.includes('Core'));
  };

  // Get backup default label based on product type
  const getBackupDefaultLabel = (productName) => {
    if (!productName) return '2 Mingguan';
    // Core/Hyper Quantum products use "Mingguan" as default
    if (productName.includes('Core') || productName.includes('Hyper Quantum')) {
      return 'Mingguan';
    }
    // Other products (MaxMail, Proxima) use "2 Mingguan"
    return '2 Mingguan';
  };

  // Check if backup variant exists (with or without velocity)
  const hasBackupVariant = useCallback((products, basePidOrProduct) => {
    // Handle both PID (number) and product object
    let baseName;
    if (typeof basePidOrProduct === 'object' && basePidOrProduct.name) {
      // Product object passed
      baseName = extractBaseName(basePidOrProduct.name);
    } else {
      // PID passed, find product
      const baseProduct = products.find(p => p.pid === basePidOrProduct);
      if (!baseProduct) {
        console.log('hasBackupVariant: Base product not found for PID:', basePidOrProduct);
        return false;
      }
      baseName = extractBaseName(baseProduct.name);
    }
    
    console.log('hasBackupVariant: Looking for backup variant with base name:', baseName);
    
    // Check if there's any product with same base name and backup
    const backupVariant = products.find(p => {
      const pName = p.name;
      const hasBackup = pName.includes('+ Backup Harian');
      const matchesBase = extractBaseName(pName) === baseName;
      return hasBackup && matchesBase;
    });
    
    console.log('hasBackupVariant: Backup variant found:', !!backupVariant, backupVariant ? backupVariant.name : 'none');
    
    return !!backupVariant;
  }, [extractBaseName]);

  // Check if velocity variant exists (with or without backup)
  const hasVelocityVariant = useCallback((products, basePidOrProduct) => {
    // Handle both PID (number) and product object
    let baseName;
    if (typeof basePidOrProduct === 'object' && basePidOrProduct.name) {
      // Product object passed
      baseName = extractBaseName(basePidOrProduct.name);
    } else {
      // PID passed, find product
      const baseProduct = products.find(p => p.pid === basePidOrProduct);
      if (!baseProduct) {
        console.log('hasVelocityVariant: Base product not found for PID:', basePidOrProduct);
        return false;
      }
      baseName = extractBaseName(baseProduct.name);
    }
    
    console.log('hasVelocityVariant: Looking for velocity variant with base name:', baseName);
    
    // Check if there's any product with same base name and velocity
    const velocityVariant = products.find(p => {
      const pName = p.name;
      const hasVelocity = pName.includes('+ Velocity');
      const matchesBase = extractBaseName(pName) === baseName;
      return hasVelocity && matchesBase;
    });
    
    console.log('hasVelocityVariant: Velocity variant found:', !!velocityVariant, velocityVariant ? velocityVariant.name : 'none');
    
    return !!velocityVariant;
  }, [extractBaseName]);

  // Calculate additional cost for addons
  const calculateAddonCost = useCallback((baseProduct, addonProduct, billingCycle) => {
    if (!baseProduct || !addonProduct || !billingCycle) return 0;
    
    const normalizedCycle = normalizeBillingCycle(billingCycle);
    const basePrice = parseFloat(baseProduct.pricing?.IDR?.[normalizedCycle] || 0);
    const addonPrice = parseFloat(addonProduct.pricing?.IDR?.[normalizedCycle] || 0);
    
    // Calculate the difference
    const costDiff = addonPrice - basePrice;
    
    // Convert to monthly cost for display
    const cycleMonths = {
      'monthly': 1,
      'quarterly': 3,
      'semiannually': 6,
      'annually': 12,
      'biennially': 24,
      'triennially': 36
    };
    
    const months = cycleMonths[normalizedCycle] || 1;
    const monthlyCost = months > 0 ? costDiff / months : 0;
    
    return Math.round(monthlyCost);
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const sid = params.get("serviceid");
    const tid = params.get("targetid");

    if (sid) {
      // If serviceid is provided, directly fetch upgrade options
      setServiceid(sid);
      
      // Send both serviceid and targetid to API
      const requestParams = { serviceid: sid };
      if (tid) {
        const parsedTid = parseInt(tid);
        requestParams.targetid = parsedTid;
        setTargetid(parsedTid);
        targetidRef.current = parsedTid; // Store in ref
      }
      
      dispatch(getUpgradeOptions(requestParams));
    } else {
      // If no serviceid, try to get client services if user is logged in
      dispatch(getClientServices());
    }

    // Set targetid if provided (even if no serviceid yet)
    if (tid && !sid) {
      const parsedTid = parseInt(tid);
      setTargetid(parsedTid);
      targetidRef.current = parsedTid; // Store in ref
    }

    // Fetch product groups for group name mapping
    dispatch(getProductGroups());
  }, [location.search, dispatch]);

  // Reload data when user logs in
  useEffect(() => {
    if (isAuthenticated) {
      if (serviceid) {
        // Send both serviceid and targetid to API
        const requestParams = { serviceid };
        // Use ref to get original targetid, not the cleared state
        if (targetidRef.current) {
          requestParams.targetid = targetidRef.current;
        }
        dispatch(getUpgradeOptions(requestParams));
      } else {
        dispatch(getClientServices());
      }
    }
  }, [isAuthenticated, dispatch, serviceid]); // Remove targetid from dependency to prevent re-fetch when cleared

  // Check if current service is Monitime product (PID 1146), Email product, or Special PID 1157
  useEffect(() => {
    if (currentService) {
      // Check for Monitime product (PID 1146)
      if (currentService.pid === 1146) {
        setIsMonitimeProduct(true);
        setIsEmailProduct(false);
        setIsSpecialProduct1157(false);
      } else if (currentService.pid === 1157) {
        // Special product PID 1157
        setIsMonitimeProduct(false);
        setIsEmailProduct(false);
        setIsSpecialProduct1157(true);
      } else {
        setIsMonitimeProduct(false);
        setIsSpecialProduct1157(false);

        // Check for Email products with configoptions (from product_details)
        const hasEmailConfigOptionsFromProduct = currentService?.product_details?.configoptions?.configoption?.some(config =>
          config.name === "Jumlah Akun" || config.name === "Email Accounts" || config.name === "Akun"
        );

        // Check for Email products with configoptions (from service configoptions)
        const hasEmailConfigOptionsFromService = currentService?.configoptions?.configoption?.some(config =>
          config.option === "Jumlah Akun" || config.option === "Email Accounts" || config.option === "Akun"
        );

        setIsEmailProduct(hasEmailConfigOptionsFromProduct || hasEmailConfigOptionsFromService);
      }
    } else {
      setIsMonitimeProduct(false);
      setIsEmailProduct(false);
      setIsSpecialProduct1157(false);
    }
  }, [currentService]);

  // Calculate Monitime upgrade price when config option value changes
  useEffect(() => {
    if (isMonitimeProduct && currentService) {
      const currentConfigValue = currentService.product_details?.configoptions?.configoption?.[0]?.value || 0;
      const configOption = currentService.product_details?.configoptions?.configoption?.[0];

      if (configOption && configOptionValue > (currentConfigValue + 1)) {
        // Calculate price difference based on billing cycle
        const billingCycle = normalizeBillingCycle(currentService.billingcycle);
        const pricePerMonitor = configOption.options?.option?.[0]?.pricing?.IDR?.[billingCycle] || 60000;
        const monitorDifference = configOptionValue - (currentConfigValue + 1);
        const totalPrice = monitorDifference * pricePerMonitor;

        setMonitimeUpgradePrice(totalPrice);
      } else {
        setMonitimeUpgradePrice(0);
      }
    }
  }, [isMonitimeProduct, currentService, configOptionValue]);

  // Calculate Email upgrade price when config option value changes
  useEffect(() => {
    if (isEmailProduct && currentService) {
      const emailConfigOption = currentService?.product_details?.configoptions?.configoption?.find(config =>
        config.name === "Jumlah Akun" || config.name === "Email Accounts" || config.name === "Akun"
      );

      if (emailConfigOption && configOptionValue > 0) {
        // For email products, use actual value (no n-1 formula)
        const currentConfigValue = currentService?.configoptions?.configoption?.find(c => c.id === emailConfigOption.id)?.value || emailConfigOption.value || 0;

        if (configOptionValue > currentConfigValue) {
          // Calculate price difference based on billing cycle
          const billingCycle = normalizeBillingCycle(currentService.billingcycle);
          const pricePerAccount = emailConfigOption.options?.option?.[0]?.pricing?.IDR?.[billingCycle] || 0;
          const accountDifference = configOptionValue - currentConfigValue;
          const totalPrice = accountDifference * pricePerAccount;

          setEmailUpgradePrice(totalPrice);
        } else {
          setEmailUpgradePrice(0);
        }
      } else {
        setEmailUpgradePrice(0);
      }
    }
  }, [isEmailProduct, currentService, configOptionValue]);

  // Calculate Special PID 1157 upgrade price when config option value changes
  useEffect(() => {
    if (isSpecialProduct1157 && currentService && upgradeToMonitime) {
      // Get PID 1146 product details for pricing
      const billingCycle = normalizeBillingCycle(special1157BillingCycle);

      // Calculate base upgrade price (PID 1157 to PID 1146)
      const currentPrice = parseFloat(calculateCurrentPrice(currentService, false, false));

      // Get PID 1146 pricing from API data
      const targetProductPricing = {
        monthly: 10000,
        quarterly: 30000,
        semiannually: 60000,
        annually: 120000,
        biennially: 240000,
        triennially: 0
      };

      // Get target product price based on selected billing cycle
      const targetProductPrice = targetProductPricing[billingCycle] || 0;

      // Get config options pricing for PID 1146
      const configOptionPricing = {
        monthly: 10000,
        quarterly: 30000,
        semiannually: 60000,
        annually: 120000,
        biennially: 240000,
        triennially: 0
      };

      const configOptionPrice = configOptionPricing[billingCycle] || 0;
      const configPrice = (special1157ConfigValue - 1) * configOptionPrice; // n-1 formula

      const totalUpgradePrice = (targetProductPrice - currentPrice) + configPrice;

      setSpecial1157UpgradePrice(totalUpgradePrice > 0 ? totalUpgradePrice : 0);
    } else {
      setSpecial1157UpgradePrice(0);
    }
  }, [isSpecialProduct1157, currentService, upgradeToMonitime, special1157ConfigValue, special1157BillingCycle]);

  // Set default config option value when Monitime or Email product is detected
  useEffect(() => {
    if (isMonitimeProduct && currentService) {
      const currentConfigValue = currentService.product_details?.configoptions?.configoption?.[0]?.value || 0;
      // Set to current value + 2 as default (to show a difference of 1 monitor)
      setConfigOptionValue(currentConfigValue + 2);
    } else if (isEmailProduct && currentService) {
      const emailConfigOption = currentService?.product_details?.configoptions?.configoption?.find(config =>
        config.name === "Jumlah Akun" || config.name === "Email Accounts" || config.name === "Akun"
      );

      if (emailConfigOption) {
        // For email products, use actual value (no n-1 formula)
        const currentConfigValue = currentService?.configoptions?.configoption?.find(c => c.id === emailConfigOption.id)?.value || emailConfigOption.value || 0;
        // Set to current value + 1 as default (to show a difference of 1 account)
        setConfigOptionValue(currentConfigValue + 1);
      }
    }
  }, [isMonitimeProduct, isEmailProduct, currentService]);

  // Set default config option value when Special PID 1157 product is detected
  useEffect(() => {
    if (isSpecialProduct1157 && currentService) {
      setSpecial1157ConfigValue(1); // Default to 1 monitor
      setUpgradeToMonitime(false); // Default to unchecked
      setSpecial1157BillingCycle(currentService.billingcycle || 'monthly'); // Default to current billing cycle
    }
  }, [isSpecialProduct1157, currentService]);

  // Filter products to remove addon variants from dropdown
  useEffect(() => {
    if (selectedGroup && selectedGroup.products) {
      const filtered = selectedGroup.products.filter(prod => 
        !prod.name.includes('+ Velocity') && 
        !prod.name.includes('+ Backup Harian') &&
        !prod.name.includes('+ Snapshot')
      );
      
      // If no base products found (all products have addons), 
      // try to use current service product as base for display
      if (filtered.length === 0 && selectedGroup.products.length > 0 && currentService) {
        console.log('No base products in list, using current service as base...');
        const currentBaseName = extractBaseName(currentService.productname);
        
        // Check if current service product_details is available and can be used
        if (currentService.product_details) {
          setFilteredProducts([{
            ...currentService.product_details,
            name: currentBaseName,
            _isCurrentService: true // Flag to identify this is current service
          }]);
          console.log('Using current service as base product:', currentBaseName);
        } else {
          // Fallback: show all products (including addon variants)
          console.log('No current service details, showing all products including addons');
          setFilteredProducts(selectedGroup.products);
        }
      } else {
        setFilteredProducts(filtered);
      }
      
      // Reset velocity and backup options when group changes (unless auto-selecting or addon already selected)
      // Don't reset if we're auto-selecting OR if selected product has addons
      const hasAddonProduct = selectedProduct && (
        selectedProduct.name.includes('+ Velocity') || 
        selectedProduct.name.includes('+ Backup Harian')
      );
      
      if (!isAutoSelecting && !hasAddonProduct) {
        console.log('Resetting addon options because group changed (not auto-selecting)');
        setVelocityEnabled(false);
        setBackupOption('mingguan');
      } else {
        console.log('Preserving addon options:', {
          isAutoSelecting,
          hasAddonProduct: !!hasAddonProduct,
          selectedProduct: selectedProduct?.name
        });
      }
    } else {
      setFilteredProducts([]);
    }
  }, [selectedGroup, extractBaseName, isAutoSelecting, currentService, selectedProduct]);

  // Auto-select target product when targetid is provided
  useEffect(() => {
    // Wait for filteredProducts to be populated
    if (targetid && upgradeOptions && upgradeOptions.length > 0 && currentService && filteredProducts.length > 0) {
      // Find the group and product that matches the targetid
      let targetGroup = null;
      let targetProduct = null;

      for (const group of upgradeOptions) {
        if (group.products && Array.isArray(group.products)) {
          const foundProduct = group.products.find(p => p.pid === targetid);
          if (foundProduct) {
            targetGroup = group;
            targetProduct = foundProduct;
            break;
          }
        }
      }

      if (targetGroup && targetProduct) {
        console.log('Auto-selecting target product:', targetProduct.name, 'PID:', targetProduct.pid);
        
        // Set flag to prevent override during auto-selection
        setIsAutoSelecting(true);
        
        // Auto-select the group first
        dispatch(setSelectedGroup(targetGroup));
        
        // Determine base product and addons from target product name
        const targetName = targetProduct.name;
        const hasVelocity = targetName.includes('+ Velocity');
        const hasBackup = targetName.includes('+ Backup Harian');
        
        console.log('Target product analysis:', {
          name: targetName,
          hasVelocity,
          hasBackup
        });
        
        // Find the base product (without addons) for the target
        const baseName = extractBaseName(targetName);
        console.log('Base name extracted:', baseName);
        
        // Strategy: Find base product from filteredProducts (which may include current service)
        let baseProduct = filteredProducts.find(p => {
          const pBaseName = extractBaseName(p.name);
          return pBaseName === baseName;
        });
        
        // If not found in filtered list, try finding in all products
        if (!baseProduct) {
          console.log('Base product not found in filtered list, trying all products...');
          baseProduct = targetGroup.products.find(p => {
            const pName = p.name;
            const pBaseName = extractBaseName(pName);
            return pBaseName === baseName && 
                   !pName.includes('+ Velocity') && 
                   !pName.includes('+ Backup Harian');
          });
        }
        
        // If still not found, use the first filtered product (likely current service)
        if (!baseProduct && filteredProducts.length > 0) {
          console.log('Using first filtered product as base:', filteredProducts[0].name);
          baseProduct = filteredProducts[0];
        }
        
        console.log('Base product found:', baseProduct ? `${baseProduct.name} (PID: ${baseProduct.pid})` : 'Not found');
        
        // Set base product
        if (baseProduct) {
          setBaseSelectedProduct(baseProduct);
          console.log('✓ Using base product for dropdown:', baseProduct.name, 'PID:', baseProduct.pid);
        } else {
          // Extreme fallback
          console.log('⚠ No base product found at all, using target as reference');
          setBaseSelectedProduct(targetProduct);
        }
        
        // Set addon states based on target product
        setVelocityEnabled(hasVelocity);
        setBackupOption(hasBackup ? 'harian' : 'mingguan');
        
        console.log('Addon states set:', {
          velocityEnabled: hasVelocity,
          backupOption: hasBackup ? 'harian' : 'mingguan'
        });
        
        // Preserve billing cycle before dispatching setSelectedProduct
        preservedBillingCycleRef.current = normalizeBillingCycle(currentService.billingcycle);
        
        // Auto-select the target product (which already has the addons in its name) with preserveBillingCycle flag
        console.log('Auto-selecting target product directly:', targetProduct.name, 'PID:', targetProduct.pid);
        dispatch(setSelectedProduct({ product: targetProduct, preserveBillingCycle: true }));
        
        // Auto-select billing cycle (same as current service if available)
        const currentBillingCycle = normalizeBillingCycle(currentService.billingcycle);
        console.log('Current service billing cycle:', currentService.billingcycle, '(normalized:', currentBillingCycle, ')');
        
        // Get available billing cycles inline
        const pricing = targetProduct?.pricing?.IDR;
        const billingCycleHierarchy = ['monthly', 'quarterly', 'semiannually', 'annually', 'biennially', 'triennially'];
        const currentCycleIndex = billingCycleHierarchy.indexOf(currentBillingCycle);
        const availableCycles = [];
        
        if (pricing) {
          Object.keys(pricing).forEach(cycle => {
            if (cycle !== 'prefix' && cycle !== 'suffix' &&
              cycle !== 'msetupfee' && cycle !== 'qsetupfee' &&
              cycle !== 'ssetupfee' && cycle !== 'asetupfee' &&
              cycle !== 'bsetupfee' && cycle !== 'tsetupfee') {
              const price = parseFloat(pricing[cycle]);
              if (price > 0) {
                const normalizedCycle = normalizeBillingCycle(cycle);
                const cycleIndex = billingCycleHierarchy.indexOf(normalizedCycle);
                // DISABLED: Upgrade termin ke lebih tinggi dinonaktifkan
                // Hanya tampilkan termin yang sama dengan termin saat ini
                // Sebelumnya: const isEnabled = cycleIndex >= currentCycleIndex; // Allow same or higher
                const isEnabled = cycleIndex === currentCycleIndex; // Only allow same billing cycle
                
                // Hanya tambahkan ke list jika termin sama dengan termin saat ini
                if (isEnabled) {
                  availableCycles.push({
                    cycle: cycle,
                    normalizedCycle: normalizedCycle,
                    price: price,
                    isEnabled: isEnabled
                  });
                }
              }
            }
          });
        }
        
        console.log('Available billing cycles:', availableCycles);
        
        if (availableCycles.length > 0) {
          // Try to find the same billing cycle as current service
          console.log('Looking for billing cycle:', currentBillingCycle);
          console.log('Available cycles detail:', availableCycles.map(c => ({
            cycle: c.cycle,
            normalized: c.normalizedCycle,
            price: c.price,
            enabled: c.isEnabled,
            matches: c.normalizedCycle === currentBillingCycle
          })));
          
          const matchingCycle = availableCycles.find(c => 
            c.normalizedCycle === currentBillingCycle && c.isEnabled
          );
          
          if (matchingCycle) {
            console.log('✓ Matching billing cycle found:', matchingCycle.cycle, 'Price:', matchingCycle.price);
            console.log('✓ Setting billing cycle for product:', targetProduct.name, 'PID:', targetProduct.pid);
            dispatch(setSelectedBillingCycle(matchingCycle.cycle));
          } else {
            // If not found, select the first enabled cycle
            console.log('⚠ No matching cycle found, looking for first enabled...');
            const firstEnabledCycle = availableCycles.find(c => c.isEnabled);
            if (firstEnabledCycle) {
              console.log('Using first enabled cycle:', firstEnabledCycle.cycle, 'Price:', firstEnabledCycle.price);
              console.log('✓ Setting billing cycle for product:', targetProduct.name, 'PID:', targetProduct.pid);
              dispatch(setSelectedBillingCycle(firstEnabledCycle.cycle));
            } else {
              console.log('❌ No enabled cycles found!');
            }
          }
        }
        
        // Clear targetid and auto-selecting flag after auto-selection
        console.log('Auto-selection complete, clearing targetid and flag after delay');
        setTimeout(() => {
          setIsAutoSelecting(false);
          setTargetid(null);
          console.log('Auto-selection flag cleared');
        }, 200); // Small delay to ensure all states are set
      } else {
        console.log('Target product not found for targetid:', targetid);
        setIsAutoSelecting(false);
      }
    }
  }, [targetid, upgradeOptions, currentService, filteredProducts, dispatch, extractBaseName]);

  // Ensure baseSelectedProduct is set when selectedProduct exists but baseSelectedProduct is null
  useEffect(() => {
    if (selectedProduct && !baseSelectedProduct && selectedGroup) {
      // Extract base product from selectedProduct
      const selectedName = selectedProduct.name;
      const baseName = extractBaseName(selectedName);
      
      // Find base product in the group
      const baseProduct = selectedGroup.products.find(p => {
        const pName = p.name;
        return extractBaseName(pName) === baseName && 
               !pName.includes('+ Velocity') && 
               !pName.includes('+ Backup Harian');
      });
      
      if (baseProduct) {
        setBaseSelectedProduct(baseProduct);
        
        // Also set addon states based on selectedProduct name
        const hasVelocity = selectedName.includes('+ Velocity');
        const hasBackup = selectedName.includes('+ Backup Harian');
        setVelocityEnabled(hasVelocity);
        setBackupOption(hasBackup ? 'harian' : 'mingguan');
      }
    }
  }, [selectedProduct, baseSelectedProduct, selectedGroup, extractBaseName]);

  useEffect(() => {
    if (error) {
      // Check if error contains redirect_to_invoice information
      if (error.redirect_to_invoice && error.invoice_info?.invoiceid) {
        setRedirectToInvoice({
          invoiceid: error.invoice_info.invoiceid,
          redirect_url: error.redirect_url,
          message: error.message,
          upgrade_id: error.upgrade_info?.upgrade_id,
          upgrade_status: error.upgrade_info?.upgrade_status
        });
      } else if (error.pending_upgrade && error.upgrade_info?.invoice_info?.invoiceid) {
        // Handle the new API response structure
        setRedirectToInvoice({
          invoiceid: error.upgrade_info.invoice_info.invoiceid,
          redirect_url: error.redirect_url,
          message: error.message,
          upgrade_id: error.upgrade_info.upgrade_id,
          upgrade_status: error.upgrade_info.upgrade_status
        });
      } else {
        notifyError(error);
      }
    }
  }, [error]);

  const handleGroupSelect = (group) => {
    dispatch(setSelectedGroup(group));
  };

  const handleProductSelect = (product) => {
    console.log('handleProductSelect called with product:', product.name, 'PID:', product.pid);
    
    // Store the base product (without addons)
    setBaseSelectedProduct(product);
    
    // Reset addon options when selecting a new base product
    setVelocityEnabled(false);
    setBackupOption('mingguan');
    
    // Set the base product as selected (don't preserve billing cycle for manual selection)
    console.log('Dispatching setSelectedProduct for manual selection');
    dispatch(setSelectedProduct({ product: product, preserveBillingCycle: false }));
  };

  const handleServiceSelect = (service) => {
    // Check if service is active
    if (service.status !== 'Active') {
      notifyError("Hanya service dengan status Active yang dapat diupgrade");
      return;
    }

    dispatch(setSelectedService(service));
    // Use 'id' field instead of 'serviceid' as per the API response structure
    const serviceId = service.id || service.serviceid;
    if (!serviceId) {
      notifyError("Invalid service ID");
      return;
    }
    setServiceid(serviceId.toString());
    setMonitimeUpgradePrice(0);
    setEmailUpgradePrice(0);
    setSpecial1157UpgradePrice(0);
    setIsMonitimeProduct(false);
    setIsEmailProduct(false);
    setIsSpecialProduct1157(false);
    setUpgradeToMonitime(false);
    setSpecial1157ConfigValue(1);
    setSpecial1157BillingCycle('monthly');
    setShowCalculationDetails(false);
    setShowAllServices(false);
    
    // Reset Velocity and Backup options
    setVelocityEnabled(false);
    setBackupOption('mingguan');
    setBaseSelectedProduct(null);
    setFilteredProducts([]);
    setVelocityCost(0);
    setBackupCost(0);
    setIsAutoSelecting(false);
    
    // Clear refs when manually selecting from service list
    targetidRef.current = null;
    preservedBillingCycleRef.current = null;
    
    // Call API without targetid when selecting from service list
    dispatch(getUpgradeOptions({ serviceid: serviceId.toString() }));
  };

  const handleBillingCycleSelect = (billingCycle) => {
    console.log('Manual billing cycle selection:', billingCycle);
    dispatch(setSelectedBillingCycle(billingCycle));
  };

  // Check if upgrade is valid (not a downgrade)
  const isValidUpgrade = (selectedProduct, selectedBillingCycle) => {
    if (!currentService || !selectedProduct || !selectedBillingCycle) return true;

    const currentPrice = parseFloat(calculateCurrentPrice(currentService, isMonitimeProduct, isEmailProduct));
    const normalizedBillingCycle = normalizeBillingCycle(selectedBillingCycle);
    const newPrice = parseFloat(selectedProduct.pricing?.IDR?.[normalizedBillingCycle] || 0);

    // console.log('isValidUpgrade Debug:', {
    //   currentService: currentService?.productname,
    //   selectedProduct: selectedProduct?.name,
    //   selectedBillingCycle,
    //   normalizedBillingCycle,
    //   currentPrice,
    //   newPrice,
    //   isValid: newPrice > currentPrice
    // });

    return newPrice > currentPrice;
  };

  const handleAddToCart = () => {
    // Check if current service is active
    if (currentService && currentService.status !== 'Active') {
      notifyError("Hanya service dengan status Active yang dapat diupgrade");
      return;
    }

    if (isMonitimeProduct) {
      // For Monitime products, validate config option value
      const currentConfigValue = currentService.product_details?.configoptions?.configoption?.[0]?.value || 0;
      if (configOptionValue <= (currentConfigValue + 1)) {
        notifyError("Jumlah monitor baru harus lebih besar dari jumlah monitor saat ini");
        return;
      }
    } else if (isEmailProduct) {
      // For Email products, validate config option value
      const emailConfigOption = currentService?.product_details?.configoptions?.configoption?.find(config =>
        config.name === "Jumlah Akun" || config.name === "Email Accounts" || config.name === "Akun"
      );

      if (emailConfigOption) {
        const currentConfigValue = currentService?.configoptions?.configoption?.find(c => c.id === emailConfigOption.id)?.value || emailConfigOption.value || 0;
        if (configOptionValue <= currentConfigValue) {
          notifyError("Jumlah akun baru harus lebih besar dari jumlah akun saat ini");
          return;
        }
      }
    } else if (isSpecialProduct1157) {
      // For Special PID 1157 products, validate upgrade selection
      if (!upgradeToMonitime) {
        notifyError("Silakan pilih upgrade ke Monitime terlebih dahulu");
        return;
      }

      if (!special1157BillingCycle) {
        notifyError("Silakan pilih termin pembayaran terlebih dahulu");
        return;
      }

      if (special1157ConfigValue < 1) {
        notifyError("Jumlah monitor minimal adalah 1");
        return;
      }
    } else {
      // For regular products, validate product and billing cycle selection
      if (!selectedProduct || !selectedBillingCycle) {
        notifyError("Please select a product and billing cycle");
        return;
      }

      // Check if this is a valid upgrade (not a downgrade)
      if (!isValidUpgrade(selectedProduct, selectedBillingCycle)) {
        notifyError("Maaf Produk ini tidak dapat anda pilih tidak memenuhi Syarat Upgrade");
        return;
      }
    }

    let upgradeProduct;

    if (isMonitimeProduct) {
      // For Monitime products, create config options upgrade
      const currentConfigValue = currentService.product_details?.configoptions?.configoption?.[0]?.value || 0;
      const configOptionId = currentService.product_details?.configoptions?.configoption?.[0]?.id || 1;

      upgradeProduct = {
        type: "upgrade",
        pid: currentService.pid,
        name: currentService.productname,
        billingcycle: currentService.billingcycle,
        price: monitimeUpgradePrice,
        serviceid: currentService.serviceid || currentService.id,
        currentService: currentService,
        upgradeType: "configoptions",
        configOptionId: configOptionId,
        configOptionValue: configOptionValue - 1, // Apply n-1 formula: quantity 1 = 0 monitors, quantity 2 = 1 monitor, etc.
        upgradeDetails: {
          fromProduct: `${currentService.productname} (${currentConfigValue + 1} Monitor)`,
          toProduct: `${currentService.productname} (${configOptionValue} Monitor)`,
          fromBillingCycle: currentService.billingcycle,
          toBillingCycle: currentService.billingcycle,
          remainingDays: remainingDays,
          upgradePrice: monitimeUpgradePrice,
        },
      };
    } else if (isEmailProduct) {
      // For Email products, create config options upgrade
      const emailConfigOption = currentService?.product_details?.configoptions?.configoption?.find(config =>
        config.name === "Jumlah Akun" || config.name === "Email Accounts" || config.name === "Akun"
      );

      if (emailConfigOption) {
        // For email products, use actual value (no n-1 formula)
        const currentConfigValue = currentService?.configoptions?.configoption?.find(c => c.id === emailConfigOption.id)?.value || emailConfigOption.value || 0;
        const configOptionId = emailConfigOption.id;
        const optionId = emailConfigOption.options?.option?.[0]?.id || 0; // Get the actual option ID

        upgradeProduct = {
          type: "upgrade",
          pid: currentService.pid,
          name: currentService.productname,
          billingcycle: currentService.billingcycle,
          price: emailUpgradePrice,
          serviceid: currentService.serviceid || currentService.id,
          currentService: currentService,
          upgradeType: "configoptions",
          configOptionId: configOptionId,
          configOptionValue: configOptionValue, // For email, use actual value (no n-1 formula)
          optionId: optionId, // Add the actual option ID
          upgradeDetails: {
            fromProduct: `${currentService.productname} (${currentConfigValue} Akun)`,
            toProduct: `${currentService.productname} (${configOptionValue} Akun)`,
            fromBillingCycle: currentService.billingcycle,
            toBillingCycle: currentService.billingcycle,
            remainingDays: remainingDays,
            upgradePrice: emailUpgradePrice,
          },
        };
      }
    } else if (isSpecialProduct1157) {
      // For Special PID 1157 products, create combined package + config upgrade
      upgradeProduct = {
        type: "upgrade",
        pid: 1146, // Target PID
        name: "Monitime", // Target product name
        billingcycle: special1157BillingCycle,
        price: special1157UpgradePrice,
        serviceid: currentService.serviceid || currentService.id,
        currentService: currentService,
        upgradeType: "package_and_config",
        targetPid: 1146,
        configOptionId: 115, // Monitime config option ID
        configOptionValue: special1157ConfigValue - 1, // Apply n-1 formula
        upgradeDetails: {
          fromProduct: `${currentService.productname}`,
          toProduct: `Monitime (${special1157ConfigValue} Monitor)`,
          fromBillingCycle: currentService.billingcycle,
          toBillingCycle: special1157BillingCycle,
          remainingDays: remainingDays,
          upgradePrice: special1157UpgradePrice,
        },
      };
    } else {
      // For regular products
      const currentPrice = parseFloat(calculateCurrentPrice(currentService, isMonitimeProduct, isEmailProduct));
      const normalizedBillingCycle = normalizeBillingCycle(selectedBillingCycle);
      const newPrice = parseFloat(selectedProduct.pricing?.IDR?.[normalizedBillingCycle] || 0);
      const calculatedUpgradePrice = newPrice - currentPrice;

      upgradeProduct = {
        type: "upgrade",
        pid: selectedProduct.pid,
        name: selectedProduct.name,
        billingcycle: selectedBillingCycle,
        price: upgradePrice > 0 ? upgradePrice : calculatedUpgradePrice,
        serviceid: currentService.serviceid || currentService.id,
        currentService: currentService,
        upgradeType: "package",
        upgradeDetails: {
          fromProduct: currentService.productname,
          toProduct: selectedProduct.name,
          fromBillingCycle: currentService.billingcycle,
          toBillingCycle: selectedBillingCycle,
          remainingDays: remainingDays,
          upgradePrice: upgradePrice > 0 ? upgradePrice : calculatedUpgradePrice,
        },
      };
    }

    dispatch(addToCart(upgradeProduct));
    notifySuccess("Upgrade product added to cart");
    // navigate("/paymentmethod");
  };

  const getBillingCycleLabel = (cycle) => {
    const labels = {
      'monthly': '1 Bulan',
      'quarterly': '3 Bulan',
      'semiannually': '6 Bulan',
      'semi-annually': '6 Bulan',
      'annually': '1 Tahun',
      'biennially': '2 Tahun',
      'triennially': '3 Tahun'
    };

    // Ensure lowercase and handle edge cases
    const normalizedCycle = String(cycle).toLowerCase().trim();

    // Handle common variations
    if (normalizedCycle === 'annual') return '1 Tahun';
    if (normalizedCycle === 'biennial') return '2 Tahun';
    if (normalizedCycle === 'triennial') return '3 Tahun';
    if (normalizedCycle === 'semi-annually') return '6 Bulan';

    return labels[normalizedCycle] || normalizedCycle;
  };

  // Calculate addon costs when base product or billing cycle changes
  useEffect(() => {
    if (!baseSelectedProduct || !selectedGroup || !selectedBillingCycle) {
      setVelocityCost(0);
      setBackupCost(0);
      return;
    }
    
    const allProducts = selectedGroup.products;
    
    // Calculate Velocity cost
    const velocityProduct = findVelocityProduct(allProducts, baseSelectedProduct.pid);
    if (velocityProduct) {
      const cost = calculateAddonCost(baseSelectedProduct, velocityProduct, selectedBillingCycle);
      setVelocityCost(cost);
    } else {
      setVelocityCost(0);
    }
    
    // Calculate Backup cost
    const backupProduct = findBackupProduct(allProducts, baseSelectedProduct.pid);
    if (backupProduct) {
      const cost = calculateAddonCost(baseSelectedProduct, backupProduct, selectedBillingCycle);
      setBackupCost(cost);
    } else {
      setBackupCost(0);
    }
  }, [baseSelectedProduct, selectedGroup, selectedBillingCycle, findVelocityProduct, findBackupProduct, calculateAddonCost]);

  // Update selected product when velocity or backup option changes
  useEffect(() => {
    // Skip if auto-selecting to prevent override
    if (isAutoSelecting) {
      console.log('🔄 Skipping product update during auto-selection');
      return;
    }
    
    if (!baseSelectedProduct || !selectedGroup) {
      console.log('⚠️ Skipping addon useEffect: baseSelectedProduct or selectedGroup not ready');
      return;
    }
    
    const allProducts = selectedGroup.products;
    
    // Determine the desired addon combination
    const wantVelocity = velocityEnabled;
    const wantBackup = backupOption === 'harian';
    
    console.log('🔄 Addon useEffect triggered:', {
      isAutoSelecting,
      baseProductName: baseSelectedProduct.name,
      baseProductPid: baseSelectedProduct.pid,
      wantVelocity,
      wantBackup,
      currentSelectedProductName: selectedProduct?.name,
      currentSelectedProductPid: selectedProduct?.pid,
      currentBillingCycle: selectedBillingCycle
    });
    
    // Check if selectedProduct already matches desired addon states
    if (selectedProduct) {
      const currentHasVelocity = selectedProduct.name.includes('+ Velocity');
      const currentHasBackup = selectedProduct.name.includes('+ Backup Harian');
      
      if (currentHasVelocity === wantVelocity && currentHasBackup === wantBackup) {
        console.log('✅ selectedProduct already matches desired addon states, no update needed');
        return;
      }
    }
    
    // Find product with the exact addon combination
    const targetProduct = findProductWithAddons(
      allProducts, 
      baseSelectedProduct.pid, 
      wantVelocity, 
      wantBackup
    );
    
    console.log('🔍 findProductWithAddons result:', targetProduct ? `${targetProduct.name} (PID: ${targetProduct.pid})` : 'NOT FOUND');
    
    // If found and different from current, update
    if (targetProduct && targetProduct.pid !== selectedProduct?.pid) {
      console.log('✅ Switching to product with addons:', targetProduct.name, 'PID:', targetProduct.pid);
      
      // Preserve billing cycle before switching
      if (selectedBillingCycle) {
        console.log('💾 Preserving billing cycle:', selectedBillingCycle);
        preservedBillingCycleRef.current = selectedBillingCycle;
      }
      
      // Dispatch with preserveBillingCycle flag
      dispatch(setSelectedProduct({ product: targetProduct, preserveBillingCycle: true }));
      
      // Restore billing cycle after product switch (backup mechanism)
      if (preservedBillingCycleRef.current) {
        console.log('🔄 Restoring billing cycle via timeout:', preservedBillingCycleRef.current);
        setTimeout(() => {
          dispatch(setSelectedBillingCycle(preservedBillingCycleRef.current));
          preservedBillingCycleRef.current = null;
        }, 50);
      }
    } else if (!targetProduct && selectedProduct?.pid !== baseSelectedProduct.pid) {
      // If no matching product found, fallback to base product
      console.log('⚠️ No matching product found, fallback to base');
      
      // Preserve billing cycle
      if (selectedBillingCycle) {
        preservedBillingCycleRef.current = selectedBillingCycle;
      }
      
      // Dispatch with preserveBillingCycle flag
      dispatch(setSelectedProduct({ product: baseSelectedProduct, preserveBillingCycle: true }));
      
      // Restore billing cycle (backup mechanism)
      if (preservedBillingCycleRef.current) {
        setTimeout(() => {
          dispatch(setSelectedBillingCycle(preservedBillingCycleRef.current));
          preservedBillingCycleRef.current = null;
        }, 50);
      }
    } else {
      console.log('ℹ️ No product update needed - target same as current or not found');
    }
  }, [velocityEnabled, backupOption, baseSelectedProduct, selectedGroup, selectedProduct, selectedBillingCycle, dispatch, findProductWithAddons, isAutoSelecting]);

  // Re-validate and update billing cycle when selectedProduct changes (due to addon changes)
  useEffect(() => {
    if (!selectedProduct || !selectedBillingCycle || !currentService) {
      return;
    }
    
    // Skip during auto-selection
    if (isAutoSelecting) {
      console.log('🔄 Skipping billing cycle validation during auto-selection');
      return;
    }
    
    console.log('🔍 Validating billing cycle for product:', selectedProduct.name, 'Current cycle:', selectedBillingCycle);
    
    // Get available cycles for the new product
    const pricing = selectedProduct.pricing?.IDR;
    if (!pricing) {
      console.log('⚠️ No pricing found for product');
      return;
    }
    
    const currentBillingCycle = normalizeBillingCycle(currentService.billingcycle);
    const selectedNormalized = normalizeBillingCycle(selectedBillingCycle);
    
    // Check if current billing cycle is available and enabled for the new product
    const price = parseFloat(pricing[selectedNormalized]);
    const isAvailable = price && price > 0;
    
    // DISABLED: Upgrade termin ke lebih tinggi dinonaktifkan
    // Hanya tampilkan termin yang sama dengan termin saat ini
    // Sebelumnya: Check if it's enabled (same or higher than current service billing cycle)
    const billingCycleHierarchy = ['monthly', 'quarterly', 'semiannually', 'annually', 'biennially', 'triennially'];
    const currentCycleIndex = billingCycleHierarchy.indexOf(currentBillingCycle);
    const selectedCycleIndex = billingCycleHierarchy.indexOf(selectedNormalized);
    // Sebelumnya: const isEnabled = selectedCycleIndex >= currentCycleIndex; // Allow same or higher
    const isEnabled = selectedCycleIndex === currentCycleIndex; // Only allow same billing cycle
    
    console.log('📊 Billing cycle validation:', {
      selectedBillingCycle,
      selectedNormalized,
      price,
      isAvailable,
      isEnabled,
      currentCycleIndex,
      selectedCycleIndex
    });
    
    if (!isAvailable || !isEnabled) {
      console.log('❌ Current billing cycle not available/enabled, finding alternative...');
      
      // Find the closest available and enabled billing cycle
      const availableCycles = [];
      Object.keys(pricing).forEach(cycle => {
        if (cycle !== 'prefix' && cycle !== 'suffix' &&
          cycle !== 'msetupfee' && cycle !== 'qsetupfee' &&
          cycle !== 'ssetupfee' && cycle !== 'asetupfee' &&
          cycle !== 'bsetupfee' && cycle !== 'tsetupfee') {
          const cyclePrice = parseFloat(pricing[cycle]);
          if (cyclePrice > 0) {
            const normalizedCycle = normalizeBillingCycle(cycle);
            const cycleIndex = billingCycleHierarchy.indexOf(normalizedCycle);
            // DISABLED: Upgrade termin ke lebih tinggi dinonaktifkan
            // Hanya tampilkan termin yang sama dengan termin saat ini
            // Sebelumnya: const isCycleEnabled = cycleIndex >= currentCycleIndex; // Allow same or higher
            const isCycleEnabled = cycleIndex === currentCycleIndex; // Only allow same billing cycle
            
            if (isCycleEnabled) {
              availableCycles.push({
                cycle: cycle,
                normalizedCycle: normalizedCycle,
                price: cyclePrice,
                index: cycleIndex
              });
            }
          }
        }
      });
      
      if (availableCycles.length > 0) {
        // Sort by index to get closest cycle
        availableCycles.sort((a, b) => a.index - b.index);
        
        // Try to find the same cycle first
        const sameCycle = availableCycles.find(c => c.normalizedCycle === currentBillingCycle);
        const newCycle = sameCycle || availableCycles[0];
        
        console.log('✅ Setting new billing cycle:', newCycle.cycle, 'Price:', newCycle.price);
        dispatch(setSelectedBillingCycle(newCycle.cycle));
      } else {
        console.log('❌ No available billing cycles found, resetting...');
        dispatch(setSelectedBillingCycle(null));
      }
    } else {
      console.log('✅ Billing cycle still valid for new product');
      // Re-dispatch to trigger price recalculation
      console.log('🔄 Re-dispatching billing cycle to trigger recalculation');
      dispatch(setSelectedBillingCycle(selectedBillingCycle));
    }
  }, [selectedProduct, dispatch, currentService, selectedBillingCycle, isAutoSelecting]);

  // Get group name from API data
  const getGroupName = (gid, fallbackName) => {
    if (productGroups && productGroups.length > 0) {
      const group = productGroups.find(g => g.id === gid);
      if (group) {
        return group.name;
      }
    }
    return fallbackName || `Group ${gid}`;
  };

  const getBillingCycleOptions = (product) => {
    if (!product?.pricing?.IDR) return [];

    const pricing = product.pricing.IDR;
    const cycles = [];
    const currentBillingCycle = normalizeBillingCycle(currentService?.billingcycle);

    // Define billing cycle hierarchy (higher index = higher billing cycle)
    const billingCycleHierarchy = ['monthly', 'quarterly', 'semiannually', 'annually', 'biennially', 'triennially'];

    Object.keys(pricing).forEach(cycle => {
      if (cycle !== 'prefix' && cycle !== 'suffix' &&
        cycle !== 'msetupfee' && cycle !== 'qsetupfee' &&
        cycle !== 'ssetupfee' && cycle !== 'asetupfee' &&
        cycle !== 'bsetupfee' && cycle !== 'tsetupfee') {
        const price = parseFloat(pricing[cycle]);
        if (price > 0) {
          // Normalize cycle for comparison
          const normalizedCycle = normalizeBillingCycle(cycle);
          const currentCycleIndex = billingCycleHierarchy.indexOf(currentBillingCycle);
          const cycleIndex = billingCycleHierarchy.indexOf(normalizedCycle);

          // DISABLED: Upgrade termin ke lebih tinggi dinonaktifkan
          // Hanya tampilkan termin yang sama dengan termin saat ini
          // Sebelumnya: const isEnabled = cycleIndex >= currentCycleIndex; // Allow same or higher
          const isEnabled = cycleIndex === currentCycleIndex; // Only allow same billing cycle

          // Hanya tambahkan ke list jika termin sama dengan termin saat ini
          if (isEnabled) {
            cycles.push({
              cycle: cycle,
              price: price,
              displayName: cycle.charAt(0).toUpperCase() + cycle.slice(1),
              isEnabled: isEnabled
            });
          }
        }
      }
    });

    return cycles;
  };

  if (status === "loading" || clientServicesStatus === "loading" || productGroupsStatus === "loading") {
    return (
      <MainLayouts>
        <div className="text-center py-5">
          <div className="mb-4">
            <div className="bg-light rounded-circle d-inline-flex align-items-center justify-content-center mb-3" style={{ width: '80px', height: '80px' }}>
              <Spinner animation="border" size="lg" className="text-primary" />
            </div>
            <h5 className="text-muted">Memuat Data Upgrade</h5>
            <p className="text-muted">Mohon tunggu sebentar...</p>
          </div>
        </div>
      </MainLayouts>
    );
  }

  // Removed service status check to allow all statuses for upgrade

  return (
    <MainLayouts summary={false}>
      <div className="upgrade-page">
        {/* Redirect to Invoice Alert */}
        {redirectToInvoice && (
          <CardWrapper title="Upgrade Sedang Pending">
            <Alert variant="warning" className="mb-4">
              <div className="d-flex align-items-center">
                <FontAwesomeIcon icon={faExclamationTriangle} className="me-3 fs-4" />
                <div>
                  <h6 className="mb-1">Service ini memiliki upgrade yang sedang pending</h6>
                  <p className="mb-0 small">{redirectToInvoice.message}</p>
                </div>
              </div>
            </Alert>

            <div className="row g-4">
              <div className="col-md-6">
                <Card className="border-warning border-opacity-25">
                  <Card.Body className="p-4">
                    <h6 className="text-warning mb-3">
                      <FontAwesomeIcon icon={faExclamationTriangle} className="me-2" />
                      Informasi Upgrade
                    </h6>
                    {redirectToInvoice.upgrade_id && (
                      <div className="d-flex justify-content-between align-items-center mb-2">
                        <span className="text-muted">Upgrade ID:</span>
                        <span className="fw-bold">{redirectToInvoice.upgrade_id}</span>
                      </div>
                    )}
                    {redirectToInvoice.upgrade_status && (
                      <div className="d-flex justify-content-between align-items-center mb-2">
                        <span className="text-muted">Status:</span>
                        <span className="badge bg-warning">{redirectToInvoice.upgrade_status}</span>
                      </div>
                    )}
                    <div className="d-flex justify-content-between align-items-center">
                      <span className="text-muted">Invoice ID:</span>
                      <span className="fw-bold text-primary">{redirectToInvoice.invoiceid}</span>
                    </div>
                  </Card.Body>
                </Card>
              </div>

              <div className="col-md-6">
                <Card className="border-primary border-opacity-25">
                  <Card.Body className="p-4 text-center">
                    <h6 className="text-primary mb-3">Aksi yang Tersedia</h6>
                    <div className="d-grid gap-2">
                      <Button
                        variant="primary"
                        size="lg"
                        onClick={() => window.open(`${process.env.REACT_APP_BASE_API}viewinvoice.php?id=${redirectToInvoice.invoiceid}`, '_blank')}
                      >
                        <FontAwesomeIcon icon={faArrowUp} className="me-2" />
                        Bayar Invoice
                      </Button>
                      <Button
                        variant="outline-secondary"
                        onClick={() => {
                          setRedirectToInvoice(null);
                          navigate('/upgrade');
                        }}
                      >
                        Kembali ke List Service
                      </Button>
                    </div>
                  </Card.Body>
                </Card>
              </div>
            </div>
          </CardWrapper>
        )}

        {/* Services with Unpaid Invoices Alert - HIDDEN to avoid client distraction */}
        {/* 
        {servicesWithUnpaidInvoices && servicesWithUnpaidInvoices.length > 0 && (
          <CardWrapper title="Service dengan Pending Upgrade">
            <Alert variant="info" className="mb-3">
              <FontAwesomeIcon icon={faExclamationTriangle} className="me-2" />
              <strong>Beberapa service memiliki upgrade yang sedang pending atau invoice yang belum dibayar.</strong>
            </Alert>
            <div className="mb-3">
              {servicesWithUnpaidInvoices.map((service, index) => (
                <div key={index} className="d-flex justify-content-between align-items-center p-3 border rounded mb-2">
                  <div>
                    <div className="mb-1">
                      <strong>{service.productname}</strong>
                  </div>
                    <div className="small text-muted">
                      Status: <span className="badge bg-warning">{service.upgrade_status || service.issue_type}</span>
                      {service.upgrade_id && (
                        <span className="ms-2">Upgrade ID: {service.upgrade_id}</span>
                      )}
                      {service.orderid && (
                        <span className="ms-2">Order ID: {service.orderid}</span>
                      )}
                    </div>
                  </div>
                  <div>
                    {service.invoice_info && service.invoice_info.invoiceid ? (
                  <Button 
                        variant="primary"
                    size="sm"
                        onClick={() => window.open(`${process.env.REACT_APP_BASE_API}viewinvoice.php?id=${service.invoice_info.invoiceid}`, '_blank')}
                  >
                        Bayar Invoice #{service.invoice_info.invoiceid}
                  </Button>
                    ) : service.upgrade_info?.invoice_info?.invoiceid ? (
                      <Button
                        variant="primary"
                        size="sm"
                        onClick={() => window.open(`${process.env.REACT_APP_BASE_API}viewinvoice.php?id=${service.upgrade_info.invoice_info.invoiceid}`, '_blank')}
                      >
                        Bayar Invoice #{service.upgrade_info.invoice_info.invoiceid}
                      </Button>
                    ) : service.orderid ? (
                      <Button
                        variant="outline-warning"
                        size="sm"
                        disabled
                      >
                        Invoice Belum Dibuat
                      </Button>
                    ) : (
                      <Button
                        variant="outline-info"
                        size="sm"
                        disabled
                      >
                        Menunggu Proses
                      </Button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </CardWrapper>
        )}
        */}
        {/* Service Selection - Show when no serviceid provided and user has services */}
        {!serviceid && clientServices.length > 0 && !redirectToInvoice && (
          <CardWrapper
            title="Pilih Layanan untuk Upgrade"
            titleExtra={
              <Button
                variant="outline-secondary"
                size="sm"
                onClick={() => setShowAllServices(!showAllServices)}
              >
                {showAllServices ? 'Sembunyikan Non-Active' : 'Tampilkan Semua'}
              </Button>
            }
          >
            {/* Search Input */}
            <div className="mb-3">
              <div className="position-relative">
                <FontAwesomeIcon 
                  icon={faSearch} 
                  className="position-absolute"
                  style={{ left: '15px', top: '50%', transform: 'translateY(-50%)', color: '#6c757d', zIndex: 10 }}
                />
                <input
                  type="text"
                  className="form-control ps-5"
                  placeholder="Cari layanan berdasarkan nama atau domain..."
                  value={serviceSearchQuery}
                  onChange={(e) => setServiceSearchQuery(e.target.value)}
                  style={{ fontSize: '14px' }}
                />
                {serviceSearchQuery && (
                  <button
                    className="btn btn-sm position-absolute"
                    style={{ right: '5px', top: '50%', transform: 'translateY(-50%)', border: 'none', background: 'transparent', color: '#6c757d' }}
                    onClick={() => setServiceSearchQuery('')}
                  >
                    ×
                  </button>
                )}
              </div>
            </div>

            {(() => {
              const filteredServices = clientServices.filter(service => {
                // Filter by status
                const statusMatch = showAllServices || service.status === 'Active';
                
                // Filter by search query
                if (!serviceSearchQuery.trim()) {
                  return statusMatch;
                }
                
                const query = serviceSearchQuery.toLowerCase().trim();
                const serviceName = (service.name || service.productname || '').toLowerCase();
                const domain = (service.domain || '').toLowerCase();
                
                return statusMatch && (serviceName.includes(query) || domain.includes(query));
              });

              if (filteredServices.length === 0) {
                return (
                  <div className="text-center py-5">
                    <FontAwesomeIcon icon={faSearch} className="text-muted mb-2" style={{ fontSize: '48px' }} />
                    <p className="text-muted mb-0">
                      {serviceSearchQuery ? (
                        <>Tidak ada layanan yang cocok dengan "<strong>{serviceSearchQuery}</strong>"</>
                      ) : (
                        'Tidak ada layanan yang tersedia'
                      )}
                    </p>
                    {serviceSearchQuery && (
                      <Button
                        variant="link"
                        size="sm"
                        className="mt-2"
                        onClick={() => setServiceSearchQuery('')}
                      >
                        Hapus pencarian
                      </Button>
                    )}
                  </div>
                );
              }

              return (
                <Row className="g-4">
                  {filteredServices.map((service, index) => {
                  const isActive = service.status === 'Active';
                  const isMonitime = service.pid === 1146 || service.pid === 1157;
                  const hasDomain = service.domain && service.domain.trim() !== '';
                  return (
                    <Col key={index} lg={6} md={12}>
                      <Card
                        className={`h-100 service-card ${isActive ? 'cursor-pointer' : 'cursor-not-allowed'} ${selectedService?.id === service.id ? 'border-primary border-3' : ''} ${!isActive ? 'opacity-60' : ''}`}
                        onClick={() => isActive && handleServiceSelect(service)}
                        style={{
                          pointerEvents: isActive ? 'auto' : 'none',
                          backgroundColor: !isActive ? '#f8f9fa' : 'white'
                        }}
                      >
                        <Card.Body className="p-3">
                          <div className="d-flex justify-content-between align-items-start mb-2">
                            <div className="flex-grow-1">
                              <h6 className="mb-1 fw-bold service-product-name">
                                {service.name || service.productname}
                              </h6>
                              <div className="d-flex align-items-center gap-1">
                                {hasDomain ? (
                                  <span className="badge service-domain-badge service-domain-orange">{service.domain}</span>
                                ) : !isMonitime ? (
                                  <span className="badge bg-secondary service-domain-badge">Belum memiliki domain</span>
                                ) : null}
                                <span className={`badge ${
                                  isActive 
                                    ? 'bg-success' 
                                    : service.status?.toLowerCase() === 'terminated' 
                                      ? 'service-status-terminated' 
                                      : 'bg-secondary'
                                } service-status-badge`}>
                                  {service.status}
                                </span>
                              </div>
                            </div>
                            {selectedService?.id === service.id && (
                              <div className="text-primary ms-2">
                                <FontAwesomeIcon icon={faArrowUp} />
                              </div>
                            )}
                          </div>

                          <div className="row g-2">
                            <div className="col-6">
                              <div className="bg-light p-2 rounded service-info-box">
                                <small className="text-muted d-block mb-1">Termin</small>
                                <span className="fw-bold service-info-value">{getBillingCycleLabel(service.billingcycle)}</span>
                              </div>
                            </div>
                            <div className="col-6">
                              <div className="bg-light p-2 rounded service-info-box">
                                <small className="text-muted d-block mb-1">Harga</small>
                                <span className="fw-bold service-price-value">{formatIDR(calculateCurrentPrice(service, service.pid === 1146,
                                  service?.configoptions?.configoption?.some(config =>
                                    config.option === "Jumlah Akun" || config.option === "Email Accounts" || config.option === "Akun"
                                  ) || service?.product_details?.configoptions?.configoption?.some(config =>
                                    config.name === "Jumlah Akun" || config.name === "Email Accounts" || config.name === "Akun"
                                  )
                                ))}</span>
                              </div>
                            </div>
                            <div className="col-12">
                              <div className="bg-light p-2 rounded service-info-box">
                                <small className="text-muted d-block mb-1">Jatuh Tempo</small>
                                <span className="fw-bold service-info-value">
                                  {service.pid === 1157 ?
                                    (() => {
                                      // Untuk PID 1157, gunakan registration date + 14 hari
                                      const regDate = service.regdate || service.created_at || service.date;
                                      if (regDate) {
                                        const dueDate = new Date(regDate);
                                        dueDate.setDate(dueDate.getDate() + 14);
                                        return dueDate.toISOString().split('T')[0];
                                      }
                                      return service.nextduedate;
                                    })()
                                    : service.nextduedate
                                  }
                                </span>
                              </div>
                            </div>
                          </div>

                          {isActive && (
                            <div className="mt-2 text-center">
                              <small className="text-success service-upgrade-hint">
                                <FontAwesomeIcon icon={faArrowUp} className="me-1" />
                                Klik untuk upgrade
                              </small>
                              {/* {service.pid === 1157 && (
                                <div className="mt-1">
                                  <small className="text-info">
                                    Sisa: {(() => {
                                      // Untuk PID 1157, hitung dari registration date + 14 hari
                                      const regDate = service.regdate || service.created_at || service.date;
                                      if (regDate) {
                                        const dueDate = new Date(regDate);
                                        dueDate.setDate(dueDate.getDate() + 14);
                                        const today = new Date();
                                        const diffTime = dueDate - today;
                                        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
                                        return diffDays > 0 ? diffDays : 0;
                                      }
                                      return 0;
                                    })()} hari
                              </small>
                                </div>
                              )} */}
                            </div>
                          )}
                        </Card.Body>
                      </Card>
                    </Col>
                  );
                  })}
                </Row>
              );
            })()}
            {clientServices.some(service => service.status !== 'Active') && (
              <div className="mt-4 text-center">
                <small className="text-muted">
                  <FontAwesomeIcon icon={faExclamationTriangle} className="me-1" />
                  {showAllServices ? (
                    <>
                      Menampilkan semua layanan ({clientServices.length} total)
                      <Button
                        variant="link"
                        size="sm"
                        className="p-0 ms-1"
                        onClick={() => setShowAllServices(false)}
                      >
                        Sembunyikan non-Active
                      </Button>
                    </>
                  ) : (
                    <>
                      {clientServices.filter(service => service.status !== 'Active').length} layanan non-Active tersembunyi.
                      <Button
                        variant="link"
                        size="sm"
                        className="p-0 ms-1"
                        onClick={() => setShowAllServices(true)}
                      >
                        Tampilkan semua
                      </Button>
                    </>
                  )}
                </small>
              </div>
            )}
          </CardWrapper>
        )}

        {/* Show login required message when user is not authenticated */}
        {!isAuthenticated && !redirectToInvoice && (
          <CardWrapper title="Upgrade Layanan">
            <div className="text-center py-5">
              <div className="mb-4">
                <div className="bg-light rounded-circle d-inline-flex align-items-center justify-content-center mb-3" style={{ width: '80px', height: '80px' }}>
                  <FontAwesomeIcon icon={faExclamationTriangle} size="2x" className="text-warning" />
                </div>
                <h5 className="text-muted">Login Diperlukan</h5>
                <p className="text-muted">Untuk mengakses fitur Upgrade silahkan Login terlebih dahulu...</p>
              </div>
            </div>
          </CardWrapper>
        )}

        {/* Show message when no services available (only when authenticated) */}
        {isAuthenticated && !serviceid && clientServices.length === 0 && clientServicesStatus === "idle" && !redirectToInvoice && (
          <CardWrapper title="Upgrade Layanan">
            <div className="text-center py-5">
              <div className="mb-4">
                <div className="bg-light rounded-circle d-inline-flex align-items-center justify-content-center mb-3" style={{ width: '80px', height: '80px' }}>
                  <FontAwesomeIcon icon={faArrowUp} size="2x" className="text-muted" />
                </div>
                <h5 className="text-muted">Tidak Ada Layanan Tersedia</h5>
                <p className="text-muted">Tidak ada layanan yang dapat diupgrade saat ini.</p>
              </div>
            </div>
          </CardWrapper>
        )}

        {/* Show error message for client services (only when authenticated) */}
        {isAuthenticated && !serviceid && clientServicesStatus === "error" && !redirectToInvoice && (
          <CardWrapper title="Upgrade Layanan">
            <div className="text-center py-5">
              <div className="mb-4">
                <div className="bg-light rounded-circle d-inline-flex align-items-center justify-content-center mb-3" style={{ width: '80px', height: '80px' }}>
                  <FontAwesomeIcon icon={faExclamationTriangle} size="2x" className="text-warning" />
                </div>
                <h5 className="text-muted">Terjadi Kesalahan</h5>
                <p className="text-muted">Gagal memuat data layanan. Silakan coba lagi.</p>
              </div>
            </div>
          </CardWrapper>
        )}

        {/* Current Service Information */}
        {isAuthenticated && currentService && serviceid && (
          <CardWrapper
            title="Informasi Paket Saat Ini"
            titleExtra={
              <Button
                variant="outline-secondary"
                size="sm"
                onClick={() => {
                  dispatch(resetUpgradeSelection());
                  setServiceid(null);
                  setTargetid(null);
                  targetidRef.current = null; // Clear targetid ref
                  preservedBillingCycleRef.current = null; // Clear billing cycle ref
                  setConfigOptionValue(1);
                  setMonitimeUpgradePrice(0);
                  setIsMonitimeProduct(false);
                  setShowCalculationDetails(false);
                  setShowAllServices(false);
                  
                  // Reset Velocity and Backup options
                  setVelocityEnabled(false);
                  setBackupOption('mingguan');
                  setBaseSelectedProduct(null);
                  setFilteredProducts([]);
                  setVelocityCost(0);
                  setBackupCost(0);
                  
                  navigate('/upgrade');
                }}
              >
                <FontAwesomeIcon icon={faArrowLeft} className="me-1" />
                Kembali ke List Service
              </Button>
            }
          >
            {/* Show error for non-active services */}
            {currentService.status !== 'Active' && (
              <Alert variant="danger" className="mb-4">
                <FontAwesomeIcon icon={faExclamationTriangle} className="me-2" />
                <strong>Status Service: {currentService.status}</strong> - Hanya service dengan status Active yang dapat diupgrade.
              </Alert>
            )}

            <div className="row">
              <div className="col-md-12">
                <div className="d-flex align-items-center mb-3">
                  <div className="flex-grow-1">
                    <h5 className="mb-1 fw-bold">
                      {currentService.name || currentService.productname}
                      {currentService.domain && currentService.domain.trim() !== '' && (
                        <span className="text-muted"> ({currentService.domain})</span>
                      )}
                    </h5>
                    <span className={`badge ${currentService.status === 'Active' ? 'bg-success' : 'bg-warning'}`}>
                      {currentService.status}
                    </span>
                  </div>
                </div>

                <div className="row g-3">
                  <div className="col-sm-6">
                    <div className="bg-light p-3 rounded-4">
                      <div className="d-flex justify-content-between align-items-center">
                        <span className="text-muted">Termin Pembayaran:</span>
                        <span className="fw-bold">{getBillingCycleLabel(currentService.billingcycle)}</span>
                      </div>
                    </div>
                  </div>
                  <div className="col-sm-6">
                    <div className="bg-light p-3 rounded-4">
                      <div className="d-flex justify-content-between align-items-center">
                        <span className="text-muted">Harga Saat Ini:</span>
                        <span className="fw-bold" style={{ color: '#ff851b' }}>{formatIDR(calculateCurrentPrice(currentService, isMonitimeProduct, isEmailProduct))}</span>
                      </div>
                    </div>
                  </div>
                  <div className="col-sm-6">
                    <div className="bg-light p-3 rounded-4">
                      <div className="d-flex justify-content-between align-items-center">
                        <span className="text-muted">Jatuh Tempo:</span>
                        <span className="fw-bold">
                          {isSpecialProduct1157 ?
                            (() => {
                              // Untuk PID 1157, gunakan registration date + 14 hari
                              const regDate = currentService.regdate || currentService.created_at || currentService.date;
                              if (regDate) {
                                const dueDate = new Date(regDate);
                                dueDate.setDate(dueDate.getDate() + 14);
                                return dueDate.toISOString().split('T')[0];
                              }
                              return currentService.nextduedate;
                            })()
                            : currentService.nextduedate
                          }
                        </span>
                      </div>
                    </div>
                  </div>
                  <div className="col-sm-6">
                    <div className="bg-light p-3 rounded-4">
                      <div className="d-flex justify-content-between align-items-center">
                        <span className="text-muted">Sisa Masa Hari Paket Lama:</span>
                        <span className="fw-bold text-info">
                          {isSpecialProduct1157 ?
                            (() => {
                              // Untuk PID 1157, hitung dari registration date + 14 hari
                              const regDate = currentService.regdate || currentService.created_at || currentService.date;
                              if (regDate) {
                                const dueDate = new Date(regDate);
                                dueDate.setDate(dueDate.getDate() + 14);
                                const today = new Date();
                                const diffTime = dueDate - today;
                                const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
                                return diffDays > 0 ? diffDays : 0;
                              }
                              return remainingDays;
                            })()
                            : remainingDays
                          } hari
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </CardWrapper>
        )}

        {/* Upgrade Options */}
        {isAuthenticated && currentService && serviceid && currentService.status === 'Active' && (upgradeOptions.length > 0 || isMonitimeProduct || isEmailProduct || isSpecialProduct1157) ? (
          <CardWrapper
            title={isSpecialProduct1157 ? "Pilih Layanan Upgrade" : "Pilihan Upgrade"}
          >
            {/* Special PID 1157 - Pilih Layanan Upgrade Interface */}
            {isSpecialProduct1157 && (
              <div className="mb-4">
                <div className="d-flex align-items-center mb-3">
                  <h6 className="mb-0 me-3">Pilih Layanan Upgrade</h6>
                  <span className="badge bg-warning">Konfigurasi Khusus</span>
                </div>

                <Alert variant="info" className="mb-4">
                  <FontAwesomeIcon icon={faExclamationTriangle} className="me-2" />
                  <strong>Upgrade Khusus</strong> - Pilih layanan upgrade ke Monitime (Paid Version) dengan konfigurasi monitor.
                </Alert>

                {/* Simple Upgrade Card dengan Checkbox, Billing Cycle, dan Config Options */}
                <Card className="border-warning border-opacity-25">
                  <Card.Body className="p-4">
                    {/* Checkbox untuk pilih layanan upgrade ke Monitime */}
                    <div className="form-check mb-4">
                      <input
                        className="form-check-input"
                        type="checkbox"
                        id="upgradeToMonitime"
                        checked={upgradeToMonitime}
                        onChange={(e) => setUpgradeToMonitime(e.target.checked)}
                      />
                      <label className="form-check-label fw-bold" htmlFor="upgradeToMonitime">
                        Pilih Layanan: Monitime
                      </label>
                      <div className="text-muted small mt-1">
                        Pilih layanan upgrade dari produk ini ke Monitime dengan konfigurasi monitor
                      </div>
                    </div>

                    {/* Billing Cycle dan Config Options - tampil bersamaan jika checkbox dicentang */}
                    {upgradeToMonitime && (
                      <div className="row g-4">
                        {/* Billing Cycle Selection */}
                        <div className="col-12">
                          <h6 className="mb-3">Pilih Termin Pembayaran</h6>
                          <select
                            className="form-select"
                            value={special1157BillingCycle}
                            onChange={(e) => setSpecial1157BillingCycle(e.target.value)}
                          >
                            <option value="">Pilih termin pembayaran...</option>
                            {[
                              { cycle: 'monthly', price: 10000 },
                              { cycle: 'quarterly', price: 30000 },
                              { cycle: 'semiannually', price: 60000 },
                              { cycle: 'annually', price: 120000 },
                              { cycle: 'biennially', price: 240000 }
                            ].map((option) => (
                              <option key={option.cycle} value={option.cycle}>
                                {getBillingCycleLabel(option.cycle)} - Rp{option.price.toLocaleString()}
                              </option>
                            ))}
                          </select>
                        </div>

                        {/* Config Options untuk Monitor - hanya tampil jika billing cycle sudah dipilih */}
                        {special1157BillingCycle ? (
                          <div className="col-12">
                            <h6 className="mb-3">Konfigurasi Monitor</h6>
                            <Row className="g-4">
                              <Col lg={6}>
                                <Card className="h-100 border-primary border-opacity-25">
                                  <Card.Body className="text-center p-4">
                                    <div className="mb-3">
                                      <h6 className="text-muted mb-2">Jumlah Monitor Saat Ini</h6>
                                      <div className="bg-primary bg-opacity-10 rounded-circle d-inline-flex align-items-center justify-content-center" style={{ width: '60px', height: '60px' }}>
                                        <h4 className="text-primary mb-0 fw-bold">0</h4>
                                      </div>
                                    </div>
                                    <p className="text-muted mb-0">Monitor aktif</p>
                                  </Card.Body>
                                </Card>
                              </Col>
                              <Col lg={6}>
                                <Card className="h-100 border-success border-opacity-25">
                                  <Card.Body className="text-center p-4">
                                    <div className="mb-3">
                                      <h6 className="text-muted mb-2">Jumlah Monitor Baru</h6>
                                      <div className="d-flex align-items-center justify-content-center">
                                        <Button
                                          variant="outline-secondary"
                                          size="sm"
                                          className="rounded-circle"
                                          style={{ width: '40px', height: '40px' }}
                                          onClick={() => setSpecial1157ConfigValue(Math.max(1, special1157ConfigValue - 1))}
                                          disabled={special1157ConfigValue <= 1}
                                        >
                                          -
                                        </Button>
                                        <div className="mx-4">
                                          <div className="bg-success bg-opacity-10 rounded-circle d-inline-flex align-items-center justify-content-center" style={{ width: '60px', height: '60px' }}>
                                            <h4 className="text-success mb-0 fw-bold">{special1157ConfigValue}</h4>
                                          </div>
                                        </div>
                                        <Button
                                          variant="outline-secondary"
                                          size="sm"
                                          className="rounded-circle"
                                          style={{ width: '40px', height: '40px' }}
                                          onClick={() => setSpecial1157ConfigValue(special1157ConfigValue + 1)}
                                        >
                                          +
                                        </Button>
                                      </div>
                                    </div>
                                    <p className="text-muted mb-2">Monitor</p>
                                    <div className="bg-light p-2 rounded">
                                      <small className="text-muted">
                                        Harga per monitor: <strong>{formatIDR((() => {
                                          const configOptionPricing = {
                                            monthly: 10000,
                                            quarterly: 30000,
                                            semiannually: 60000,
                                            annually: 120000,
                                            biennially: 240000,
                                            triennially: 0
                                          };
                                          return configOptionPricing[special1157BillingCycle] || 0;
                                        })())}</strong> per {getBillingCycleLabel(special1157BillingCycle)}
                                      </small>
                                    </div>
                                  </Card.Body>
                                </Card>
                              </Col>
                            </Row>
                          </div>
                        ) : (
                          <div className="col-12">
                            <Alert variant="info" className="mb-0">
                              <FontAwesomeIcon icon={faExclamationTriangle} className="me-2" />
                              <strong>Langkah Selanjutnya:</strong> Silakan pilih termin pembayaran terlebih dahulu untuk melanjutkan ke konfigurasi monitor.
                            </Alert>
                          </div>
                        )}
                      </div>
                    )}
                  </Card.Body>
                </Card>

                {/* Special PID 1157 Upgrade Summary */}
                {upgradeToMonitime && special1157BillingCycle && special1157ConfigValue > 0 && (
                  <div className="mt-4">
                    <Card className="border-warning border-opacity-25">
                      <Card.Body className="p-4">
                        <div className="row">
                          <div className="col-12">
                            <div className="fw-bold mb-3 h5" style={{ color: '#ff851b' }}>
                              Ringkasan Pilihan Layanan Upgrade
                            </div>
                          </div>
                        </div>
                        <div className="col-12 text-end">
                          <div className="fw-bold h5 mb-3" style={{ color: '#ff851b' }}>
                            {special1157UpgradePrice > 0 ? formatIDR(special1157UpgradePrice) : 'Menghitung...'}
                          </div>
                        </div>

                        <div>
                          <p><strong>Dari:</strong> {currentService.productname} ({getBillingCycleLabel(currentService.billingcycle)})</p>
                          <p><strong>Ke:</strong> Monitime ({special1157ConfigValue} Monitor) ({getBillingCycleLabel(special1157BillingCycle)})</p>
                          <p><strong>Harga per Monitor:</strong> {formatIDR((() => {
                            const configOptionPricing = {
                              monthly: 10000,
                              quarterly: 30000,
                              semiannually: 60000,
                              annually: 120000,
                              biennially: 240000,
                              triennially: 0
                            };
                            return configOptionPricing[special1157BillingCycle] || 0;
                          })())}</p>
                          <p><strong>Sisa Masa Hari Paket Lama:</strong> {(() => {
                            // Untuk PID 1157, hitung dari registration date + 14 hari
                            const regDate = currentService.regdate || currentService.created_at || currentService.date;
                            if (regDate) {
                              const dueDate = new Date(regDate);
                              dueDate.setDate(dueDate.getDate() + 14);
                              const today = new Date();
                              const diffTime = dueDate - today;
                              const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
                              return diffDays > 0 ? diffDays : 0;
                            }
                            return remainingDays;
                          })()} hari</p>
                        </div>

                        {special1157UpgradePrice > 0 ? (
                          <AddToCartButton onClick={handleAddToCart} />
                        ) : (
                          <div className="text-center mt-4">
                            <small className="text-muted">
                              Menghitung harga upgrade...
                            </small>
                          </div>
                        )}
                      </Card.Body>
                    </Card>
                  </div>
                )}
              </div>
            )}

            {/* Monitime Config Options */}
            {isMonitimeProduct && (
              <div className="mb-4">
                <div className="d-flex align-items-center mb-3">
                  <h6 className="mb-0 me-3">Konfigurasi Monitor</h6>
                  <span className="badge bg-info">Produk Monitime</span>
                </div>

                <Alert variant="info" className="mb-4">
                  <FontAwesomeIcon icon={faExclamationTriangle} className="me-2" />
                  <strong>Upgrade Monitime</strong> - Upgrade dilakukan dengan mengubah jumlah monitor yang aktif.
                </Alert>

                <Row className="g-4">
                  <Col lg={6}>
                    <Card className="h-100 border-primary border-opacity-25">
                      <Card.Body className="text-center p-4">
                        <div className="mb-3">
                          <h6 className="text-muted mb-2">Jumlah Monitor Saat Ini</h6>
                          <div className="bg-primary bg-opacity-10 rounded-circle d-inline-flex align-items-center justify-content-center" style={{ width: '60px', height: '60px' }}>
                            <h4 className="text-primary mb-0 fw-bold">
                              {(currentService.product_details?.configoptions?.configoption?.[0]?.value || 0) + 1}
                            </h4>
                          </div>
                        </div>
                        <p className="text-muted mb-0">Monitor aktif</p>
                      </Card.Body>
                    </Card>
                  </Col>
                  <Col lg={6}>
                    <Card className="h-100 border-success border-opacity-25">
                      <Card.Body className="text-center p-4">
                        <div className="mb-3">
                          <h6 className="text-muted mb-2">Jumlah Monitor Baru</h6>
                          <div className="d-flex align-items-center justify-content-center">
                            <Button
                              variant="outline-secondary"
                              size="sm"
                              className="rounded-circle"
                              style={{ width: '40px', height: '40px' }}
                              onClick={() => {
                                const currentConfigValue = currentService.product_details?.configoptions?.configoption?.[0]?.value || 0;
                                setConfigOptionValue(Math.max((currentConfigValue + 2), configOptionValue - 1));
                              }}
                              disabled={configOptionValue <= (currentService.product_details?.configoptions?.configoption?.[0]?.value || 0) + 2}
                            >
                              -
                            </Button>
                            <div className="mx-4">
                              <div className="bg-success bg-opacity-10 rounded-circle d-inline-flex align-items-center justify-content-center" style={{ width: '60px', height: '60px' }}>
                                <h4 className="text-success mb-0 fw-bold">{configOptionValue}</h4>
                              </div>
                            </div>
                            <Button
                              variant="outline-secondary"
                              size="sm"
                              className="rounded-circle"
                              style={{ width: '40px', height: '40px' }}
                              onClick={() => setConfigOptionValue(configOptionValue + 1)}
                            >
                              +
                            </Button>
                          </div>
                        </div>
                        <p className="text-muted mb-2">Monitor</p>
                        <div className="bg-light p-2 rounded">
                          <small className="text-muted">
                            Harga per monitor: <strong>{formatIDR(currentService.product_details?.configoptions?.configoption?.[0]?.options?.option?.[0]?.pricing?.IDR?.[normalizeBillingCycle(currentService.billingcycle)] || 60000)}</strong> per {getBillingCycleLabel(currentService.billingcycle)}
                          </small>
                        </div>
                      </Card.Body>
                    </Card>
                  </Col>
                </Row>

                {/* Monitime Upgrade Summary */}
                {configOptionValue > 0 && (
                  <div className="mt-4">
                    <Card className="border-primary border-opacity-25">
                      <Card.Body className="p-4">
                        <div className="row">
                          <div className="col-12">
                            <div className="fw-bold mb-3 h5" style={{ color: '#ff851b' }}>
                              Ringkasan Upgrade Monitime
                            </div>
                          </div>
                        </div>
                        <div className="col-12 text-end">
                          <div className="fw-bold h5 mb-3" style={{ color: '#ff851b' }}>
                            {monitimeUpgradePrice > 0 ? formatIDR(monitimeUpgradePrice) : 'Menghitung...'}
                          </div>
                        </div>

                        <div>
                          <p><strong>Dari:</strong> {currentService.productname} ({(currentService.product_details?.configoptions?.configoption?.[0]?.value || 0) + 1} Monitor)</p>
                          <p><strong>Ke:</strong> {currentService.productname} ({configOptionValue} Monitor)</p>
                          <p><strong>Termin:</strong> {getBillingCycleLabel(currentService.billingcycle)}</p>
                          <p><strong>Harga per Monitor:</strong> {formatIDR(currentService.product_details?.configoptions?.configoption?.[0]?.options?.option?.[0]?.pricing?.IDR?.[normalizeBillingCycle(currentService.billingcycle)] || 60000)}</p>
                          <p><strong>Sisa Masa Hari Paket baru:</strong> {remainingDays} hari</p>
                        </div>

                        {monitimeUpgradePrice > 0 ? (
                          <AddToCartButton onClick={handleAddToCart} />
                        ) : (
                          <div className="text-center mt-4">
                            <small className="text-muted">
                              Menghitung harga upgrade...
                            </small>
                          </div>
                        )}
                      </Card.Body>
                    </Card>
                  </div>
                )}
              </div>
            )}

            {/* Email Config Options */}
            {isEmailProduct && (
              <div className="mb-4">
                <div className="d-flex align-items-center mb-3">
                  <h6 className="mb-0 me-3">Konfigurasi Akun Email</h6>
                  <span className="badge bg-info">Produk Email</span>
                </div>

                <Alert variant="info" className="mb-4">
                  <FontAwesomeIcon icon={faExclamationTriangle} className="me-2" />
                  <strong>Upgrade Email</strong> - Upgrade akan meningkatkan jumlah akun email yang aktif.
                </Alert>

                <Row className="g-4">
                  <Col lg={6}>
                    <Card className="h-100 border-primary border-opacity-25">
                      <Card.Body className="text-center p-4">
                        <div className="mb-3">
                          <h6 className="text-muted mb-2">Jumlah Akun Saat Ini</h6>
                          <div className="bg-primary bg-opacity-10 rounded-circle d-inline-flex align-items-center justify-content-center" style={{ width: '60px', height: '60px' }}>
                            <h4 className="text-primary mb-0 fw-bold">
                              {(() => {
                                const emailConfigOption = currentService?.product_details?.configoptions?.configoption?.find(config =>
                                  config.name === "Jumlah Akun" || config.name === "Email Accounts" || config.name === "Akun"
                                );
                                if (emailConfigOption) {
                                  return currentService?.configoptions?.configoption?.find(c => c.id === emailConfigOption.id)?.value || emailConfigOption.value || 0;
                                }
                                return 0;
                              })()}
                            </h4>
                          </div>
                        </div>
                        <p className="text-muted mb-0">Akun email aktif</p>
                      </Card.Body>
                    </Card>
                  </Col>
                  <Col lg={6}>
                    <Card className="h-100 border-success border-opacity-25">
                      <Card.Body className="text-center p-4">
                        <div className="mb-3">
                          <h6 className="text-muted mb-2">Jumlah Akun Baru</h6>
                          <div className="d-flex align-items-center justify-content-center">
                            <Button
                              variant="outline-secondary"
                              size="sm"
                              className="rounded-circle"
                              style={{ width: '40px', height: '40px' }}
                              onClick={() => {
                                const emailConfigOption = currentService?.product_details?.configoptions?.configoption?.find(config =>
                                  config.name === "Jumlah Akun" || config.name === "Email Accounts" || config.name === "Akun"
                                );
                                if (emailConfigOption) {
                                  const currentConfigValue = currentService?.configoptions?.configoption?.find(c => c.id === emailConfigOption.id)?.value || emailConfigOption.value || 0;
                                  setConfigOptionValue(Math.max((currentConfigValue + 1), configOptionValue - 1));
                                }
                              }}
                              disabled={(() => {
                                const emailConfigOption = currentService?.product_details?.configoptions?.configoption?.find(config =>
                                  config.name === "Jumlah Akun" || config.name === "Email Accounts" || config.name === "Akun"
                                );
                                if (emailConfigOption) {
                                  const currentConfigValue = currentService?.configoptions?.configoption?.find(c => c.id === emailConfigOption.id)?.value || emailConfigOption.value || 0;
                                  return configOptionValue <= (currentConfigValue + 1);
                                }
                                return true;
                              })()}
                            >
                              -
                            </Button>
                            <div className="mx-4">
                              <div className="bg-success bg-opacity-10 rounded-circle d-inline-flex align-items-center justify-content-center" style={{ width: '60px', height: '60px' }}>
                                <h4 className="text-success mb-0 fw-bold">{configOptionValue}</h4>
                              </div>
                            </div>
                            <Button
                              variant="outline-secondary"
                              size="sm"
                              className="rounded-circle"
                              style={{ width: '40px', height: '40px' }}
                              onClick={() => setConfigOptionValue(configOptionValue + 1)}
                            >
                              +
                            </Button>
                          </div>
                        </div>
                        <p className="text-muted mb-2">Akun</p>
                        <div className="bg-light p-2 rounded">
                          <small className="text-muted">
                            Harga per akun: <strong>{(() => {
                              const emailConfigOption = currentService?.product_details?.configoptions?.configoption?.find(config =>
                                config.name === "Jumlah Akun" || config.name === "Email Accounts" || config.name === "Akun"
                              );
                              if (emailConfigOption) {
                                return formatIDR(emailConfigOption.options?.option?.[0]?.pricing?.IDR?.[normalizeBillingCycle(currentService.billingcycle)] || 0);
                              }
                              return formatIDR(0);
                            })()}</strong> per {getBillingCycleLabel(currentService.billingcycle)}
                          </small>
                        </div>
                      </Card.Body>
                    </Card>
                  </Col>
                </Row>

                {/* Email Upgrade Summary */}
                {configOptionValue > 0 && (
                  <div className="mt-4">
                    <Card className="border-primary border-opacity-25">
                      <Card.Body className="p-4">
                        <div className="row">
                          <div className="col-12">
                            <div className="fw-bold mb-3 h5" style={{ color: '#ff851b' }}>
                              Ringkasan Upgrade Email
                            </div>
                          </div>
                        </div>
                        <div className="col-12 text-end">
                          <div className="fw-bold h5 mb-3" style={{ color: '#ff851b' }}>
                            {emailUpgradePrice > 0 ? formatIDR(emailUpgradePrice) : 'Menghitung...'}
                          </div>
                        </div>

                        <div>
                          <p><strong>Dari:</strong> {currentService.productname} ({(() => {
                            const emailConfigOption = currentService?.product_details?.configoptions?.configoption?.find(config =>
                              config.name === "Jumlah Akun" || config.name === "Email Accounts" || config.name === "Akun"
                            );
                            if (emailConfigOption) {
                              return currentService?.configoptions?.configoption?.find(c => c.id === emailConfigOption.id)?.value || emailConfigOption.value || 0;
                            }
                            return 0;
                          })()} Akun)</p>
                          <p><strong>Ke:</strong> {currentService.productname} ({configOptionValue} Akun)</p>
                          <p><strong>Termin:</strong> {getBillingCycleLabel(currentService.billingcycle)}</p>
                          {/* <p><strong>Harga per Akun:</strong> {(() => {
                            const emailConfigOption = currentService?.product_details?.configoptions?.configoption?.find(config => 
                              config.name === "Jumlah Akun" || config.name === "Email Accounts" || config.name === "Akun"
                            );
                            if (emailConfigOption) {
                              return formatIDR(emailConfigOption.options?.option?.[0]?.pricing?.IDR?.[currentService.billingcycle.toLowerCase()] || 0);
                            }
                            return formatIDR(0);
                          })()}</p> */}
                          <p><strong>Sisa Masa Hari Paket Lama:</strong> {remainingDays} hari</p>
                        </div>

                        {emailUpgradePrice > 0 ? (
                          <AddToCartButton onClick={handleAddToCart} />
                        ) : (
                          <div className="text-center mt-4">
                            <small className="text-muted">
                              Menghitung harga upgrade...
                            </small>
                          </div>
                        )}
                      </Card.Body>
                    </Card>
                  </div>
                )}
              </div>
            )}

            {/* Group Selection - Only show for non-Monitime, non-Email, and non-Special PID 1157 products */}
            {!isMonitimeProduct && !isEmailProduct && !isSpecialProduct1157 && upgradeOptions.length > 0 && (
              <div className="mb-4">
                <label htmlFor="groupSelect" className="form-label fw-bold mb-2">
                  Pilih Grup Produk:
                </label>
                <select
                  id="groupSelect"
                  className="form-select"
                  value={selectedGroup?.gid || ''}
                  onChange={(e) => {
                    const selectedGroupId = e.target.value;
                    const group = upgradeOptions.find(g => g.gid === parseInt(selectedGroupId));
                    if (group) {
                      handleGroupSelect(group);
                    }
                  }}
                >
                  <option value="">Pilih grup produk...</option>
                  {upgradeOptions.map((group, groupIndex) => (
                    <option key={groupIndex} value={group.gid}>
                      {getGroupName(group.gid, group.name)} ({group.products.length} produk)
                    </option>
                  ))}
                </select>
              </div>
            )}

            {/* Product Selection - Only show for non-Monitime, non-Email, and non-Special PID 1157 products */}
            {!isMonitimeProduct && !isEmailProduct && !isSpecialProduct1157 && selectedGroup && (
              <div className="mb-4">
                <label htmlFor="productSelect" className="form-label fw-bold mb-2">
                  Pilih Produk:
                </label>
                <select
                  id="productSelect"
                  className="form-select"
                  value={(() => {
                    // Try to find base product in filtered list
                    if (baseSelectedProduct) {
                      const baseName = extractBaseName(baseSelectedProduct.name);
                      const matchInFiltered = filteredProducts.find(p => 
                        extractBaseName(p.name) === baseName
                      );
                      return matchInFiltered ? matchInFiltered.pid : '';
                    }
                    return '';
                  })()}
                  onChange={(e) => {
                    const selectedProductId = e.target.value;
                    const product = filteredProducts.find(p => p.pid === parseInt(selectedProductId));
                    if (product) {
                      handleProductSelect(product);
                    }
                  }}
                >
                  <option value="">Pilih produk...</option>
                  {filteredProducts.map((product, productIndex) => (
                    <option key={productIndex} value={product.pid}>
                      {product.name}
                    </option>
                  ))}
                </select>
              </div>
            )}

            {/* Backup Harian Option - Only show if base product supports backup */}
            {!isMonitimeProduct && !isEmailProduct && !isSpecialProduct1157 && baseSelectedProduct && 
             productSupportsBackup(baseSelectedProduct.name) && 
             hasBackupVariant(selectedGroup?.products || [], baseSelectedProduct) && (
              <div className="mb-4">
                <div className="d-flex justify-content-between align-items-center mb-2">
                  <label htmlFor="backupSelect" className="form-label fw-bold mb-0">
                    Opsi Backup:
                  </label>
                  {backupOption === 'harian' && backupCost > 0 && (
                    <span className="text-success fw-bold" style={{ fontSize: '14px' }}>
                      + {formatIDR(backupCost)}/bulan
                    </span>
                  )}
                </div>
                <select
                  id="backupSelect"
                  className="form-select"
                  value={backupOption}
                  onChange={(e) => setBackupOption(e.target.value)}
                >
                  <option value="mingguan">{getBackupDefaultLabel(baseSelectedProduct?.name)}</option>
                  <option value="harian">Harian {backupCost > 0 ? `(+ ${formatIDR(backupCost)}/bulan)` : ''}</option>
                </select>
                {/* {backupOption === 'harian' && backupCost > 0 && (
                  <small className="text-muted mt-1 d-block">
                    Backup harian akan menambah {formatIDR(backupCost)} per bulan
                  </small>
                )} */}
              </div>
            )}

            {/* Velocity Checkbox - Only show if base product supports velocity */}
            {!isMonitimeProduct && !isEmailProduct && !isSpecialProduct1157 && baseSelectedProduct && 
             productSupportsVelocity(baseSelectedProduct.name) && 
             hasVelocityVariant(selectedGroup?.products || [], baseSelectedProduct) && (
              <div className="mb-4">
                <div className="p-3 bg-light rounded" style={{ border: "1px solid #e0e0e0" }}>
                  <div className="d-flex justify-content-between align-items-center">
                    <div className="form-check mb-0">
                      <input
                        type="checkbox"
                        className="form-check-input"
                        id="velocityCheckbox"
                        checked={velocityEnabled}
                        onChange={(e) => setVelocityEnabled(e.target.checked)}
                        style={{ cursor: "pointer" }}
                      />
                      <label 
                        className="form-check-label fw-bold" 
                        htmlFor="velocityCheckbox"
                        style={{ fontSize: "15px", color: "#333", cursor: "pointer" }}
                      >
                        Tambah Velocity (cPanel)
                      </label>
                    </div>
                    {velocityEnabled && velocityCost > 0 && (
                      <span className="text-success fw-bold" style={{ fontSize: "14px" }}>
                        + {formatIDR(velocityCost)}/bulan
                      </span>
                    )}
                  </div>
                  <small className="text-muted d-block mt-2">
                    Velocity untuk pengguna yang ingin menggunakan panel berjenis cPanel
                    {/* {velocityCost > 0 && ` (+${formatIDR(velocityCost)}/bulan)`} */}
                  </small>
                </div>
              </div>
            )}

            {/* Billing Cycle Selection - Only show for non-Monitime, non-Email, and non-Special PID 1157 products */}
            {!isMonitimeProduct && !isEmailProduct && !isSpecialProduct1157 && selectedProduct && (
              <div className="mb-4">
                <label htmlFor="billingCycleSelect" className="form-label fw-bold mb-2">
                  Pilih Termin Pembayaran:
                </label>
                <select
                  id="billingCycleSelect"
                  className="form-select"
                  value={selectedBillingCycle || ''}
                  onChange={(e) => handleBillingCycleSelect(e.target.value)}
                >
                  <option value="">Pilih termin pembayaran...</option>
                  {getBillingCycleOptions(selectedProduct).map((cycle, cycleIndex) => (
                    <option
                      key={cycleIndex}
                      value={cycle.cycle}
                      disabled={!cycle.isEnabled}
                    >
                      Termin {getBillingCycleLabel(cycle.cycle)} - {formatIDR(cycle.price)}
                      {!cycle.isEnabled && ' (Tidak tersedia)'}
                    </option>
                  ))}
                </select>
              </div>
            )}

            {/* Downgrade Warning Alert */}
            {!isMonitimeProduct && !isEmailProduct && !isSpecialProduct1157 && selectedProduct && selectedBillingCycle && !isValidUpgrade(selectedProduct, selectedBillingCycle) && (
              <Alert variant="danger" className="mb-4">
                <FontAwesomeIcon icon={faExclamationTriangle} className="me-2" />
                <strong>Maaf Produk ini tidak dapat anda pilih tidak memenuhi Syarat Upgrade</strong>
                <div className="mt-2">
                  <small>
                    Harga produk yang dipilih ({formatIDR(selectedProduct.pricing?.IDR?.[normalizeBillingCycle(selectedBillingCycle)] || 0)})
                    lebih rendah dari harga saat ini ({formatIDR(calculateCurrentPrice(currentService, isMonitimeProduct, isEmailProduct))})
                  </small>
                </div>
              </Alert>
            )}

            {/* Upgrade Detail Comparison - Only show for non-Monitime and non-Email products */}
            {(() => {
              const shouldShowComparison = !isMonitimeProduct && !isEmailProduct && selectedProduct && selectedBillingCycle && isValidUpgrade(selectedProduct, selectedBillingCycle);
              return shouldShowComparison;
            })() && (
                <div className="mt-4">
                  <Card className="border-secondary border-opacity-25">
                    <Card.Body className="p-3">
                      <label className="fw-bold mb-2">
                        {/* <FontAwesomeIcon icon={faExclamationTriangle} className="me-2" /> */}
                        Detail Perbandingan Spesifikasi
                      </label>

                      {(() => {
                        // Get base product name for current service to handle products with addons
                        const currentBaseProductName = getBaseProductName(currentService.productname);
                        const currentSpecs = getProductSpecifications(currentBaseProductName);
                        
                        // Get base product name for new product to handle products with addons
                        const newBaseProductName = getBaseProductName(selectedProduct.name);
                        const newSpecs = getProductSpecifications(newBaseProductName);
                        
                        const comparison = compareSpecifications(currentSpecs, newSpecs);

                        // Always show comparison if newSpecs exists, even if currentSpecs is null
                        if (!newSpecs) {
                          return (
                            <div className="text-center py-3">
                              <small className="text-muted">
                                Informasi spesifikasi tidak tersedia untuk perbandingan
                              </small>
                            </div>
                          );
                        }

                        // If no comparison available, create a fallback comparison
                        if (!comparison) {
                          return (
                            <div className="text-center py-3">
                              <small className="text-muted">
                                Informasi spesifikasi tidak tersedia untuk perbandingan
                              </small>
                            </div>
                          );
                        }

                        const specKeys = [
                          'Site Hosted',
                          'Disk Space',
                          'Add On Domain',
                          'Park Domain',
                          'Sub Domain',
                          'Email Account',
                          'VCPU',
                          'RAM',
                          'Inodes'
                        ];

                        return (
                          <div className="table-responsive comparison-table">
                            <table className="table table-sm">
                              <thead>
                                <tr>
                                  <th className="text-muted small" style={{ width: '30%' }}>Spesifikasi</th>
                                  <th className="text-center" style={{ width: '35%' }}>
                                    <div className="bg-light rounded p-1 border" style={{ borderColor: '#ee4d30' }}>
                                      <div className="fw-bold text-dark small">{currentService.productname}</div>
                                      <div className="text-muted small">Paket Saat Ini</div>
                                    </div>
                                  </th>
                                  <th className="text-center" style={{ width: '35%' }}>
                                    <div className="bg-success bg-opacity-10 rounded p-1 border border-success border-opacity-25">
                                      <div className="fw-bold text-success small">{selectedProduct.name}</div>
                                      <div className="text-muted small">Paket Baru</div>
                                    </div>
                                  </th>
                                </tr>
                              </thead>
                              <tbody>
                                {specKeys.map((key) => {
                                  const spec = comparison[key];
                                  if (!spec) return null;

                                  let newIcon = null;

                                  switch (spec.type) {
                                    case 'upgrade':
                                      newIcon = <FontAwesomeIcon icon={faArrowUp} className="text-success ms-1" />;
                                      break;
                                    case 'downgrade':
                                      newIcon = <FontAwesomeIcon icon={faArrowDown} className="text-danger ms-1" />;
                                      break;
                                    case 'same':
                                      newIcon = <FontAwesomeIcon icon={faEquals} className="text-muted ms-1" />;
                                      break;
                                    default:
                                      break;
                                  }

                                  return (
                                    <tr key={key} className="align-middle">
                                      <td className="fw-bold small py-2">{key}</td>
                                      <td className="text-center small py-2">
                                        <div className="bg-light rounded px-2 py-1">
                                          {spec.current}
                                        </div>
                                      </td>
                                      <td className="text-center small py-2">
                                        <div className="bg-success bg-opacity-10 rounded px-2 py-1 border border-success border-opacity-25">
                                          {spec.new}
                                          {newIcon}
                                        </div>
                                      </td>
                                    </tr>
                                  );
                                })}
                              </tbody>
                            </table>
                          </div>
                        );
                      })()}
                    </Card.Body>
                  </Card>
                </div>
              )}

            {/* Upgrade Summary - Only show for non-Monitime and non-Email products */}
            {(() => {
              const shouldShowSummary = !isMonitimeProduct && !isEmailProduct && selectedProduct && selectedBillingCycle && isValidUpgrade(selectedProduct, selectedBillingCycle);
              // console.log('Ringkasan Upgrade Debug:', {
              //   isMonitimeProduct,
              //   isEmailProduct,
              //   selectedProduct: selectedProduct?.name,
              //   selectedBillingCycle,
              //   isValidUpgrade: isValidUpgrade(selectedProduct, selectedBillingCycle),
              //   shouldShowSummary
              // });
              return shouldShowSummary;
            })() && (
                <div className="mt-4">
                  <Card className="border-primary border-opacity-25">
                    <Card.Body className="p-4">
                      <div className="row">
                        <div className="col-12">
                          <div className="fw-bold mb-3 h5" style={{ color: '#ff851b' }}>
                            Ringkasan Upgrade
                          </div>
                        </div>
                      </div>
                      <div className="col-12 text-end">
                        <div className="fw-bold h5 mb-3" style={{ color: '#ff851b' }}>
                          {upgradePrice > 0 ? formatIDR(upgradePrice) : (() => {
                            // Fallback calculation if upgradePrice is not available
                            const currentPrice = parseFloat(calculateCurrentPrice(currentService, isMonitimeProduct, isEmailProduct));
                            const normalizedBillingCycle = normalizeBillingCycle(selectedBillingCycle);
                            const newPrice = parseFloat(selectedProduct.pricing?.IDR?.[normalizedBillingCycle] || 0);
                            const fallbackPrice = newPrice - currentPrice;
                            return fallbackPrice > 0 ? formatIDR(fallbackPrice) : 'Menghitung...';
                          })()}
                        </div>
                      </div>
                      {/* {console.log('Upgrade Price Debug:', { upgradePrice, upgradeCalculationBreakdown })} */}

                      <div>
                        <p><strong>Dari:</strong> {currentService.productname} (Termin {getBillingCycleLabel(currentService.billingcycle)})</p>
                        <p><strong>Ke:</strong> {selectedProduct.name} (Termin {getBillingCycleLabel(selectedBillingCycle)})</p>
                        <p><strong>Sisa Masa Hari Paket Lama:</strong> {remainingDays} hari</p>
                        <p><strong>Harga Paket Baru:</strong> {formatIDR(parseFloat(selectedProduct.pricing?.IDR?.[normalizeBillingCycle(selectedBillingCycle)] || 0))} per {getBillingCycleLabel(selectedBillingCycle)}</p>
                      </div>

                      {/* Calculation Breakdown */}
                      {upgradeCalculationBreakdown && (
                        <div className="mt-4">
                          <div
                            className="d-flex align-items-center cursor-pointer mb-3"
                            onClick={() => setShowCalculationDetails(!showCalculationDetails)}
                          >
                            <h6 className="mb-0 me-2">Detail Kalkulasi</h6>
                            <FontAwesomeIcon
                              icon={faChevronDown}
                              style={{ transform: showCalculationDetails ? 'rotate(180deg)' : 'rotate(0deg)' }}
                            />
                          </div>

                          {showCalculationDetails && (
                            <div className="bg-light p-3 rounded">
                              <h6 className="mb-3 text-center fw-bold" style={{ color: '#333' }}>Rincian Perhitungan Upgrade</h6>
                              
                              {(upgradeCalculationBreakdown.isBillingCycleUpgrade || upgradeCalculationBreakdown.isUpgradingToLongerTerm) ? (
                                // Billing Cycle Upgrade: Prorate + Full Payment
                                <>
                                  {/* Step 1: Prorate Sisa Masa Aktif */}
                                  <div className="mb-3">
                                    <div className="d-flex flex-column flex-sm-row justify-content-between align-items-start align-items-sm-center mb-1">
                                      <strong style={{ fontSize: '14px', marginBottom: '4px' }}>Prorate Sisa Masa Aktif</strong>
                                      <strong style={{ fontSize: '16px', color: '#dc3545', whiteSpace: 'nowrap' }}>
                                        {formatIDR(upgradeCalculationBreakdown.prorateAmount)}
                                      </strong>
                                    </div>
                                    <small className="text-muted d-block" style={{ fontSize: '12px', wordBreak: 'break-word' }}>
                                      ({formatIDR(upgradeCalculationBreakdown.newPrice)} ÷ {upgradeCalculationBreakdown.newDays} hari) × {remainingDays} hari
                                    </small>
                                  </div>

                                  {/* Step 2: Full Payment untuk Selisih Termin */}
                                  <div className="mb-3">
                                    <div className="d-flex flex-column flex-sm-row justify-content-between align-items-start align-items-sm-center mb-1">
                                      <strong style={{ fontSize: '14px', marginBottom: '4px' }}>
                                        Pembayaran Penuh Selisih Termin
                                      </strong>
                                      <strong style={{ fontSize: '16px', color: '#dc3545', whiteSpace: 'nowrap' }}>
                                        {formatIDR(upgradeCalculationBreakdown.fullPaymentAmount)}
                                      </strong>
                                    </div>
                                    <small className="text-muted d-block" style={{ fontSize: '12px', wordBreak: 'break-word' }}>
                                      ({formatIDR(upgradeCalculationBreakdown.newPrice)} ÷ {upgradeCalculationBreakdown.newDays} hari) × {upgradeCalculationBreakdown.cycleDifferenceDays || (upgradeCalculationBreakdown.newDays - upgradeCalculationBreakdown.currentDays)} hari
                                      <br />
                                      <span style={{ fontSize: '11px', fontStyle: 'italic' }}>
                                        (Selisih {upgradeCalculationBreakdown.cycleDifferenceDays || (upgradeCalculationBreakdown.newDays - upgradeCalculationBreakdown.currentDays)} hari antara termin {getBillingCycleLabel(currentService.billingcycle)} dan {getBillingCycleLabel(selectedBillingCycle)})
                                      </span>
                                    </small>
                                  </div>

                                  {/* Divider */}
                                  <hr className="my-3" style={{ borderTop: '2px solid #333' }} />

                                  {/* Result: Total Bayar */}
                                  <div className="p-3 rounded" style={{ backgroundColor: '#fff3cd', border: '2px solid #ffc107' }}>
                                    <div className="d-flex flex-column flex-sm-row justify-content-between align-items-start align-items-sm-center">
                                      <strong style={{ fontSize: '15px', color: '#333', marginBottom: '4px' }}>Total yang Harus Dibayar</strong>
                                      <strong style={{ fontSize: '20px', color: '#ff851b', whiteSpace: 'nowrap' }}>
                                        {formatIDR(upgradePrice)}
                                      </strong>
                                    </div>
                                  </div>

                                  {/* Additional Info */}
                                  <div className="mt-3">
                                    <small className="text-muted d-block mb-1">
                                      • Harga belum termasuk <strong>PPN 11%</strong>
                                    </small>
                                    <small className="text-muted d-block">
                                      <FontAwesomeIcon icon={faExclamationTriangle} className="me-1 text-danger" />
                                      <strong className="text-danger">Upgrade Termin:</strong> Prorate sisa masa aktif dengan harga termin baru + pembayaran penuh 1 tahun termin baru
                                    </small>
                                  </div>
                                </>
                              ) : (
                                // Product Upgrade: Standard WHMCS Method
                                <>
                                  {/* Step 1: Biaya Paket Baru */}
                                  <div className="mb-3">
                                    <div className="d-flex flex-column flex-sm-row justify-content-between align-items-start align-items-sm-center mb-1">
                                      <strong style={{ fontSize: '14px', marginBottom: '4px' }}>Biaya Paket Baru</strong>
                                      <strong style={{ fontSize: '16px', color: '#dc3545', whiteSpace: 'nowrap' }}>
                                        {formatIDR(upgradeCalculationBreakdown.amountDebited)}
                                      </strong>
                                    </div>
                                    <small className="text-muted d-block" style={{ fontSize: '12px', wordBreak: 'break-word' }}>
                                      ({formatIDR(upgradeCalculationBreakdown.newPrice)} ÷ {upgradeCalculationBreakdown.newDays} hari) × {remainingDays} hari
                                    </small>
                                  </div>

                                  {/* Step 2: Potongan dari Paket Lama */}
                                  <div className="mb-3">
                                    <div className="d-flex flex-column flex-sm-row justify-content-between align-items-start align-items-sm-center mb-1">
                                      <strong style={{ fontSize: '14px', marginBottom: '4px' }}>Potongan Paket Lama</strong>
                                      <strong style={{ fontSize: '16px', color: '#28a745', whiteSpace: 'nowrap' }}>
                                        - {formatIDR(upgradeCalculationBreakdown.amountCredited)}
                                      </strong>
                                    </div>
                                    <small className="text-muted d-block" style={{ fontSize: '12px', wordBreak: 'break-word' }}>
                                      ({formatIDR(upgradeCalculationBreakdown.currentPrice)} ÷ {upgradeCalculationBreakdown.currentDays} hari) × {remainingDays} hari
                                    </small>
                                  </div>

                                  {/* Divider */}
                                  <hr className="my-3" style={{ borderTop: '2px solid #333' }} />

                                  {/* Result: Total Bayar */}
                                  <div className="p-3 rounded" style={{ backgroundColor: '#fff3cd', border: '2px solid #ffc107' }}>
                                    <div className="d-flex flex-column flex-sm-row justify-content-between align-items-start align-items-sm-center">
                                      <strong style={{ fontSize: '15px', color: '#333', marginBottom: '4px' }}>Total yang Harus Dibayar</strong>
                                      <strong style={{ fontSize: '20px', color: '#ff851b', whiteSpace: 'nowrap' }}>
                                        {formatIDR(upgradePrice)}
                                      </strong>
                                    </div>
                                  </div>

                                  {/* Additional Info */}
                                  <div className="mt-3">
                                    <small className="text-muted d-block mb-1">
                                      • Harga belum termasuk <strong>PPN 11%</strong>
                                    </small>
                                    <small className="text-muted d-block">
                                      <FontAwesomeIcon icon={faExclamationTriangle} className="me-1 text-danger" />
                                      <strong className="text-danger">Prorate:</strong> Anda hanya membayar selisih harga antara kedua paket dengan sisa masa berlaku yang sama
                                    </small>
                                  </div>
                                </>
                              )}
                            </div>
                          )}
                        </div>
                      )}

                      {isValidUpgrade(selectedProduct, selectedBillingCycle) ? (
                        <AddToCartButton onClick={handleAddToCart} />
                      ) : (
                        <div className="text-center mt-4">
                          <small className="text-muted">
                            Upgrade tidak tersedia untuk produk ini
                          </small>
                        </div>
                      )}
                    </Card.Body>
                  </Card>
                </div>
              )}
          </CardWrapper>
        ) : isAuthenticated && currentService && serviceid && currentService.status === 'Active' && !isSpecialProduct1157 && !isMonitimeProduct && !isEmailProduct ? (
          <CardWrapper title="Upgrade Layanan">
            <div className="text-center py-5">
              <div className="mb-4">
                <div className="bg-light rounded-circle d-inline-flex align-items-center justify-content-center mb-3" style={{ width: '80px', height: '80px' }}>
                  <FontAwesomeIcon icon={faExclamationTriangle} size="2x" className="text-info" />
                </div>
                <h5 className="text-muted">Tidak Ada Opsi Upgrade</h5>
                <p className="text-muted">
                  Tidak ada opsi upgrade yang tersedia untuk paket ini.
                  <br />
                  <small className="text-muted">
                    Hanya produk dengan harga lebih tinggi dari paket saat ini yang ditampilkan sebagai opsi upgrade.
                  </small>
                </p>
              </div>
            </div>
          </CardWrapper>
        ) : null}
      </div>
    </MainLayouts>
  );
};

export default Upgrade;